class ArdeaTheme {
  constructor() {
    this.cart = null;
    this.imageReadyCache = new Map();
    this.productZoomReadySources = new Set();
    this.instafeedHistoryActive = false;
    this.instagramViewer = null;
    this.instagramViewerHistoryActive = false;
    this.instagramFeedElement = null;
    this.instagramFeedMutationObserver = null;
    this.instagramVideoPreloadQueue = [];
    this.instagramVideoPreloadRunning = false;
    this.textRevealObserver = null;
    this.homeScrollResizeObserver = null;
    this.aboutResizeObserver = null;
    this.productSectionRequest = null;
    this.productSectionRequestId = 0;
    this.textRevealCharset = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789?;&”$";
    this.backInStockRequestMaxAge = 90 * 24 * 60 * 60 * 1000;
    this.notificationBackendUrl = document.body.dataset.notificationBackendUrl?.replace(/\/+$/, "") || "";
    this.notificationsEnabled = this.getNotificationsEnabled();
    this.welcomePopupOpened = false;
    this.bindHeader();
    this.bindPolicyPages();
    this.bindTextReveal();
    this.bindHomeHeroVideos();
    this.bindHomeScrollControls();
    this.bindWelcomePopup();
    this.bindBackInStockModal();
    this.bindProductForms();
    this.bindSeamlessSiblingProducts();
    this.bindCartControls();
    this.bindGooglePayRecovery();
    this.bindProductGalleries();
    this.bindProductImageZoom();
    this.bindProductSizeCharts();
    this.bindAnalytics();
    this.bindInstagramVideoLoading();
    this.bindInstagramViewer();
    this.bindInstafeedLightboxHistory();
    this.bindAboutPage();
    this.refreshCart();

    document.addEventListener("shopify:section:load", (event) => {
      this.bindHomeHeroVideos(event.target);
      this.bindProductForms(event.target);
      this.bindInstagramVideoLoading();
    });
  }

  bindSeamlessSiblingProducts() {
    const initialPage = document.querySelector("[data-product-page]");
    if (!initialPage) return;

    this.replaceProductHistoryState(initialPage);

    document.addEventListener("click", (event) => {
      const link = event.target.closest("[data-sibling-product-link]");
      if (!link || event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target && link.target !== "_self") return;

      const targetUrl = new URL(link.href, window.location.href);
      if (targetUrl.origin !== window.location.origin) return;

      const currentProductUrl = new URL(
        link.closest("[data-product-page]")?.dataset.productUrl || window.location.href,
        window.location.href
      );
      if (targetUrl.pathname === currentProductUrl.pathname) {
        event.preventDefault();
        this.cancelSiblingProductSwitch();
        return;
      }

      event.preventDefault();
      this.switchSiblingProduct(targetUrl, {
        addHistoryEntry: true,
        fallbackToNavigation: true,
        initiator: link
      });
    });

    window.addEventListener("popstate", (event) => {
      if (!event.state?.ardeaProductPage) return;

      const currentPage = document.querySelector("[data-product-page]");
      const currentProductUrl = new URL(currentPage?.dataset.productUrl || window.location.href, window.location.href);
      const targetUrl = new URL(window.location.href);
      if (currentProductUrl.pathname === targetUrl.pathname) return;

      this.switchSiblingProduct(targetUrl, {
        addHistoryEntry: false,
        fallbackToNavigation: true
      });
    });
  }

  cancelSiblingProductSwitch() {
    if (!this.productSectionRequest) return;

    this.productSectionRequest.abort();
    this.productSectionRequest = null;
    this.productSectionRequestId += 1;
    const currentPage = document.querySelector("[data-product-page]");
    currentPage?.classList.remove("product-page--switching");
    currentPage?.removeAttribute("aria-busy");
    currentPage?.querySelectorAll(".color-swatch--loading").forEach((swatch) => {
      swatch.classList.remove("color-swatch--loading");
    });
    this.announceSiblingProductUpdate("Color change canceled");
  }

  replaceProductHistoryState(page) {
    const productUrl = new URL(page?.dataset.productUrl || window.location.href, window.location.href);
    window.history.replaceState({
      ...(window.history.state || {}),
      ardeaProductPage: true,
      ardeaProductPath: productUrl.pathname
    }, "", window.location.href);
  }

  async switchSiblingProduct(targetUrl, options = {}) {
    const currentPage = document.querySelector("[data-product-page]");
    const currentSection = currentPage?.closest(".shopify-section");
    const sectionId = currentPage?.dataset.productSectionId;
    if (!currentPage || !currentSection || !sectionId) {
      if (options.fallbackToNavigation) window.location.assign(targetUrl.href);
      return;
    }

    this.productSectionRequest?.abort();
    const requestController = new AbortController();
    const requestId = ++this.productSectionRequestId;
    this.productSectionRequest = requestController;
    const previousScrollY = window.scrollY;
    const previousProductUrl = new URL(currentPage.dataset.productUrl || window.location.href, window.location.href);
    const previousOptions = this.getSelectedProductOptions(currentPage);

    currentPage.classList.add("product-page--switching");
    currentPage.setAttribute("aria-busy", "true");
    options.initiator?.classList.add("color-swatch--loading");
    this.announceSiblingProductUpdate(`Loading ${options.initiator?.getAttribute("aria-label") || "color"}`);

    try {
      const requestUrl = new URL(targetUrl.href);
      requestUrl.searchParams.delete("section_id");
      requestUrl.searchParams.set("sections", sectionId);

      const response = await fetch(requestUrl, {
        credentials: "same-origin",
        signal: requestController.signal
      });
      if (!response.ok) throw new Error(`Unable to load product section (${response.status}).`);

      const sections = await response.json();
      const sectionMarkup = sections?.[sectionId];
      if (!sectionMarkup) throw new Error("Shopify returned an empty product section.");

      const template = document.createElement("template");
      template.innerHTML = sectionMarkup.trim();
      const nextSection = template.content.querySelector(".shopify-section") || template.content.firstElementChild;
      const nextPage = nextSection?.querySelector("[data-product-page]");
      if (!nextSection || !nextPage) throw new Error("The returned product section is invalid.");
      if (requestId !== this.productSectionRequestId) return;

      this.applySelectedProductOptions(nextPage, previousOptions);
      currentSection.replaceWith(nextSection);

      if (options.addHistoryEntry) {
        const nextProductUrl = new URL(nextPage.dataset.productUrl || targetUrl.href, targetUrl.href);
        window.history.pushState({
          ...(window.history.state || {}),
          ardeaProductPage: true,
          ardeaProductPath: nextProductUrl.pathname
        }, "", targetUrl.href);
      }

      this.updateSiblingProductMetadata(nextPage, targetUrl);
      const nextProductTitle = nextPage.querySelector(".product-summary h1")?.textContent.trim() || "Product";
      this.bindProductForms(nextSection);
      this.bindProductGalleries(nextSection);
      // A sibling switch updates the current page; it is not a new page entrance.
      // Shopify has already rendered the replacement section's final text.
      window.Shopify?.PaymentButton?.init?.();

      window.scrollTo({ top: previousScrollY, left: window.scrollX, behavior: "auto" });
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: previousScrollY, left: window.scrollX, behavior: "auto" });
        nextPage.querySelector(".color-swatch--active")?.focus({ preventScroll: true });
      });

      const nextProductUrl = new URL(nextPage.dataset.productUrl || targetUrl.href, targetUrl.href);
      this.announceSiblingProductUpdate(`${nextProductTitle} loaded`);
      this.captureAnalytics("$pageview");
      this.captureAnalytics("sibling_product_switched", {
        from_product_path: previousProductUrl.pathname,
        to_product_path: nextProductUrl.pathname
      });
      document.dispatchEvent(new CustomEvent("ardea:product:switched", {
        detail: {
          fromUrl: previousProductUrl.href,
          toUrl: nextProductUrl.href,
          section: nextSection
        }
      }));
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Unable to switch sibling product.", error);
      if (options.fallbackToNavigation) window.location.assign(targetUrl.href);
    } finally {
      if (requestId === this.productSectionRequestId) {
        this.productSectionRequest = null;
        document.querySelector("[data-product-page]")?.classList.remove("product-page--switching");
        document.querySelector("[data-product-page]")?.removeAttribute("aria-busy");
      }
    }
  }

  getSelectedProductOptions(page) {
    return new Map(Array.from(page.querySelectorAll("[data-option-group]")).map((group) => {
      return [group.dataset.optionName || "", group.querySelector("input:checked")?.value || ""];
    }).filter(([name, value]) => name && value));
  }

  applySelectedProductOptions(page, selectedOptions) {
    const form = page.querySelector("[data-product-form]");
    if (!form || !selectedOptions?.size) return;

    const initialSelections = Array.from(form.querySelectorAll("[data-option-group]")).map((group) => {
      return group.querySelector("input:checked");
    });

    form.querySelectorAll("[data-option-group]").forEach((group) => {
      const selectedValue = selectedOptions.get(group.dataset.optionName || "");
      const matchingInput = Array.from(group.querySelectorAll("input")).find((input) => input.value === selectedValue);
      if (matchingInput) matchingInput.checked = true;
    });

    try {
      const variants = JSON.parse(form.querySelector("[data-variants-json]")?.textContent || "[]");
      const nextSelections = Array.from(form.querySelectorAll("[data-option-group]")).map((group) => {
        return group.querySelector("input:checked")?.value || "";
      });
      const hasMatchingVariant = variants.some((variant) => {
        return variant.options.every((value, index) => value === nextSelections[index]);
      });
      if (hasMatchingVariant) return;
    } catch (_error) {
      // Keep the destination product's defaults when its variant data is invalid.
    }

    initialSelections.forEach((input) => {
      if (input) input.checked = true;
    });
  }

  updateSiblingProductMetadata(page, targetUrl) {
    if (page.dataset.documentTitle) document.title = page.dataset.documentTitle;

    const description = page.dataset.pageDescription || "";
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (description) {
      if (!descriptionMeta) {
        descriptionMeta = document.createElement("meta");
        descriptionMeta.name = "description";
        document.head.append(descriptionMeta);
      }
      descriptionMeta.content = description;
    } else {
      descriptionMeta?.remove();
    }

    const productUrl = new URL(page.dataset.productUrl || targetUrl.href, targetUrl.href);
    const canonical = document.querySelector("[data-dynamic-canonical]");
    if (canonical) canonical.href = `${productUrl.origin}${productUrl.pathname}`;
  }

  announceSiblingProductUpdate(message) {
    let status = document.querySelector("[data-sibling-product-status]");
    if (!status) {
      status = document.createElement("p");
      status.className = "visually-hidden";
      status.dataset.siblingProductStatus = "";
      status.setAttribute("role", "status");
      status.setAttribute("aria-live", "polite");
      document.body.append(status);
    }
    status.textContent = message;
  }

  captureAnalytics(eventName, properties = {}) {
    window.ardeaAnalytics?.capture(eventName, properties);
  }

  bindAnalytics() {
    const authMarkerKey = "ardea:pending-auth-event";
    const contactMarkerKey = "ardea:pending-contact-event";

    const setSessionMarker = (key, value) => {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (_error) {
        // Analytics must never interrupt the storefront flow.
      }
    };

    const takeSessionMarker = (key) => {
      try {
        const value = window.sessionStorage.getItem(key) || "";
        if (value) window.sessionStorage.removeItem(key);
        return value;
      } catch (_error) {
        return "";
      }
    };

    const classifyForm = (form) => {
      const explicitType = form.getAttribute("data-analytics-form");
      if (explicitType) return explicitType;
      if (form.matches("[data-product-form], [action*='/cart/add']")) return "product";

      const signature = [
        form.id,
        typeof form.className === "string" ? form.className : "",
        form.getAttribute("action") || "",
        form.querySelector("[name='contact[tags]']")?.value || ""
      ].join(" ").toLowerCase();

      if (signature.includes("newsletter")) return "newsletter";
      if (form.getAttribute("role") === "search" || form.matches("[action*='/search']")) return "search";
      return "form";
    };

    const formProperties = (form, formType) => {
      let formAction = window.location.pathname;
      try {
        formAction = new URL(form.getAttribute("action") || formAction, window.location.href).pathname;
      } catch (_error) {
        // Keep the current path when an app provides a malformed form action.
      }

      return {
        form_type: formType,
        form_id: form.id || undefined,
        form_action: formAction,
        form_method: (form.getAttribute("method") || "get").toLowerCase()
      };
    };

    const populateCheckoutDistinctId = () => {
      const distinctId = window.ardeaAnalytics?.getDistinctId() || "";
      if (!distinctId) return;
      document.querySelectorAll("[data-posthog-distinct-id]").forEach((input) => {
        input.value = distinctId;
      });
    };

    populateCheckoutDistinctId();
    document.addEventListener("ardea:analytics-ready", populateCheckoutDistinctId);

    document.addEventListener("submit", (event) => {
      const form = event.target.closest?.("form");
      if (!form) return;

      const formType = classifyForm(form);
      if (formType === "product") return;

      const properties = formProperties(form, formType);
      this.captureAnalytics("form submitted", properties);

      if (formType === "login" || formType === "signup") {
        setSessionMarker(authMarkerKey, formType);
      } else if (formType === "contact") {
        setSessionMarker(contactMarkerKey, "contact");
      } else if (formType === "quote") {
        this.captureAnalytics("quote requested", properties);
      } else if (formType === "newsletter") {
        this.captureAnalytics("newsletter signup submitted", properties);
      } else if (formType === "checkout" && event.submitter?.name === "checkout") {
        populateCheckoutDistinctId();
        this.captureAnalytics("checkout button clicked", {
          ...properties,
          cart_item_count: this.cart?.item_count,
          cart_value: Number(this.cart?.total_price || 0) / 100,
          currency: window.Shopify?.currency?.active
        });
      }
    });

    document.addEventListener("click", (event) => {
      const link = event.target.closest?.("a[href]");
      if (!link) return;

      let url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (_error) {
        return;
      }

      const isDownload = link.hasAttribute("download") || /\.(?:csv|docx?|pdf|pptx?|xlsx?|zip)$/i.test(url.pathname);
      if (!isDownload) return;

      this.captureAnalytics("file downloaded", {
        file_name: link.getAttribute("download") || url.pathname.split("/").pop(),
        file_url: `${url.origin}${url.pathname}`,
        link_text: link.textContent.trim().slice(0, 120) || undefined
      });
    });

    if (document.body.dataset.customerAuthenticated === "true") {
      const authEvent = takeSessionMarker(authMarkerKey);
      if (authEvent === "login") {
        this.captureAnalytics("user logged in", { auth_type: "customer_account" });
      } else if (authEvent === "signup") {
        this.captureAnalytics("user signed up", { signup_type: "customer_account" });
      }
    }

    if (document.querySelector("[data-contact-form-success]") && takeSessionMarker(contactMarkerKey)) {
      this.captureAnalytics("contact form submitted", { form_type: "contact" });
    }
  }

  getCssNumber(element, property, fallback = 0) {
    if (!element) return fallback;
    const value = parseFloat(window.getComputedStyle(element).getPropertyValue(property));
    return Number.isFinite(value) ? value : fallback;
  }

  getCssFlag(element, property) {
    return this.getCssNumber(element, property, 0) === 1;
  }

  bindHomeHeroVideos(root = document) {
    const videos = [
      ...(root.matches?.("[data-home-hero-video]") ? [root] : []),
      ...root.querySelectorAll("[data-home-hero-video]")
    ];

    videos.forEach((video) => {
      if (video.dataset.homeHeroVideoBound === "true") return;
      video.dataset.homeHeroVideoBound = "true";

      const revealVideo = () => video.classList.add("is-ready");
      video.addEventListener("loadeddata", revealVideo, { once: true });

      const poster = video.closest("[data-home-hero]")?.querySelector(".rhome-hero__poster");
      let loadStarted = false;
      let loadQueued = false;
      const loadVideo = () => {
        if (loadStarted) return;
        loadStarted = true;

        // Give the completed poster a paint before starting the much larger
        // request. It remains beneath the video if video loading fails.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          video.querySelectorAll("source[data-src]").forEach((source) => {
            source.src = source.dataset.src;
            source.removeAttribute("data-src");
          });

          video.load();
          video.play().catch(() => {});
          if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) revealVideo();
        }));
      };

      const queueVideoLoad = () => {
        if (loadQueued) return;
        loadQueued = true;

        const startAfterApps = () => {
          window.setTimeout(() => {
            if ("requestIdleCallback" in window) {
              window.requestIdleCallback(loadVideo, { timeout: 1000 });
            } else {
              loadVideo();
            }
          }, 1500);
        };

        // Shopify loads app scripts after the window load event. Waiting here
        // prevents the large hero video from delaying or competing with the
        // initial Instafeed script and thumbnail requests.
        if (document.readyState === "complete") {
          startAfterApps();
        } else {
          window.addEventListener("load", startAfterApps, { once: true });
        }
      };

      if (!poster || poster.complete) {
        queueVideoLoad();
      } else {
        poster.addEventListener("load", queueVideoLoad, { once: true });
        poster.addEventListener("error", queueVideoLoad, { once: true });
      }
    });
  }

  bindHomeScrollControls() {
    const shells = Array.from(document.querySelectorAll("[data-home-scroll-shell]"));

    if ("ResizeObserver" in window && !this.homeScrollResizeObserver) {
      this.homeScrollResizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const shell = entry.target.closest("[data-home-scroll-shell]");
          if (shell) this.updateHomeScrollButtons(shell);
        });
      });
    }

    shells.forEach((shell) => {
      const lane = shell.querySelector("[data-home-scroll-lane]");
      if (!lane) return;

      this.updateHomeScrollButtons(shell);
      lane.addEventListener("scroll", () => this.updateHomeScrollButtons(shell), { passive: true });
      this.homeScrollResizeObserver?.observe(lane);
    });

    document.addEventListener("click", (event) => {
      const button = event.target.closest("[data-home-scroll-button]");
      if (!button) return;
      if (button.disabled) return;

      const shell = button.closest("[data-home-scroll-shell]");
      const lane = shell?.querySelector("[data-home-scroll-lane]");
      if (!lane) return;

      event.preventDefault();

      const direction = button.getAttribute("data-home-scroll-button") === "prev" ? -1 : 1;
      const items = Array.from(lane.children).filter((item) => item.offsetParent !== null);
      if (!items.length) return;

      const current = lane.scrollLeft;
      const nextItem = direction > 0
        ? items.find((item) => item.offsetLeft > current + 2)
        : items.slice().reverse().find((item) => item.offsetLeft < current - 2);
      const fallbackLeft = current + direction * Math.max(1, lane.clientWidth * 0.82);
      const targetLeft = nextItem ? nextItem.offsetLeft : fallbackLeft;

      lane.scrollTo({
        left: Math.max(0, Math.min(targetLeft, lane.scrollWidth - lane.clientWidth)),
        behavior: "smooth"
      });

      window.setTimeout(() => this.updateHomeScrollButtons(shell), 420);
    });
  }

  updateHomeScrollButtons(shell) {
    const lane = shell?.querySelector("[data-home-scroll-lane]");
    if (!lane) return;

    const prev = shell.querySelector('[data-home-scroll-button="prev"]');
    const next = shell.querySelector('[data-home-scroll-button="next"]');
    const maxScroll = Math.max(0, lane.scrollWidth - lane.clientWidth);
    const isScrollable = maxScroll > 1;

    shell.classList.toggle("home-scroll-shell--scrollable", isScrollable);
    if (prev) prev.disabled = !isScrollable || lane.scrollLeft <= 1;
    if (next) next.disabled = !isScrollable || lane.scrollLeft >= maxScroll - 1;
  }

  bindTextReveal(root = document) {
    const targets = this.getTextRevealTargets(root);
    const doc = document.documentElement;

    // Temporarily force the reveal on while testing Firefox/Zen remote preview behavior.
    const reduceMotion = false;
    if (!targets.length || reduceMotion) {
      doc.classList.remove("text-reveal-prep");
      doc.classList.add("text-reveal-ready");
      return;
    }

    targets.forEach((target) => {
      if (target.dataset.textRevealBound === "true") return;

      const finalText = target.getAttribute("data-text-reveal-value") || target.textContent || "";
      const charset = target.getAttribute("data-text-reveal-charset") || this.textRevealCharset;
      target.dataset.textRevealBound = "true";
      target.dataset.textRevealValue = finalText;
      target.dataset.textRevealDelay = target.dataset.textRevealDelay || "0";
      target.dataset.textRevealDuration = target.dataset.textRevealDuration || "260";
      target.dataset.textRevealFps = target.dataset.textRevealFps || "18";
      target.dataset.textRevealStagger = target.dataset.textRevealStagger || "14";
      if (target.dataset.textRevealGenerated !== "true" && !target.getAttribute("aria-label")) {
        target.setAttribute("aria-label", finalText);
      }

      target._textRevealCharacterDelays = this.getTextRevealCharacterDelays(target, finalText);
      target.textContent = this.randomizeTextRevealValue(finalText, charset);
    });

    doc.classList.remove("text-reveal-prep");
    doc.classList.add("text-reveal-ready");

    this.observeTextRevealTargets(targets);
  }

  getTextRevealTargets(root = document) {
    const scopes = this.getTextRevealScopes(root);
    if (!scopes.length) return [];
    const targetSelector = this.getTextRevealTargetSelector();
    this.prepareTextRevealFragments(scopes, targetSelector);

    const explicitTargets = [
      ...(root.matches?.("[data-text-reveal]") ? [root] : []),
      ...root.querySelectorAll("[data-text-reveal]")
    ];
    const autoTargets = scopes.flatMap((scope) => (
      Array.from(scope.querySelectorAll(targetSelector))
    )).filter((element) => this.isTextRevealCandidate(element));

    const targets = Array.from(new Set([...explicitTargets, ...autoTargets]))
      .filter((element) => scopes.some((scope) => scope.contains(element)) && this.isTextRevealCandidate(element))
      .map((element, index) => {
        element.dataset.textRevealOrder = String(index);
        return element;
      });

    this.assignTextRevealGroupIndexes(targets);
    return targets;
  }

  getTextRevealTargetSelector() {
    return [
      "h1",
      "h2",
      "h3",
      "h4",
      ".ardea-announcement",
      "p",
      "li",
      "summary",
      "a",
      "span",
      "strong"
    ].join(",");
  }

  prepareTextRevealFragments(scopes, targetSelector) {
    scopes.forEach((scope) => {
      Array.from(scope.querySelectorAll(targetSelector)).forEach((element) => {
        if (element.dataset.textRevealGenerated === "true") return;
        if (!element.children.length || this.isTextRevealExcluded(element)) return;

        Array.from(element.childNodes).forEach((node) => {
          if (node.nodeType !== Node.TEXT_NODE) return;
          if (!node.textContent || !/[\p{L}\p{N}]/u.test(node.textContent)) return;

          const fragment = document.createElement("span");
          fragment.className = "text-reveal-fragment";
          fragment.dataset.textRevealGenerated = "true";
          fragment.textContent = node.textContent;
          node.replaceWith(fragment);
        });
      });
    });
  }

  isTextRevealExcluded(element) {
    return Boolean(element.closest([
      "form",
      "button",
      "input",
      "select",
      "textarea",
      "script",
      "style",
      "noscript",
      "svg",
      "[hidden]",
      "[aria-hidden='true']",
      "[data-text-reveal-skip]",
      ".brand",
      ".header__icon-link",
      ".header__cart-count",
      ".ardea-footer__brand",
      ".price",
      ".rhome-card__price",
      ".product-payment-stack",
      ".shopify-payment-button",
      ".product-option",
      ".sizes",
      ".swatches",
      ".rhome-card__quick",
      ".rhome-card__index",
      ".rhome-card__tag",
      ".product-card__badge",
      ".catalog-page__controls",
      ".cart-drawer",
      ".cart-lines"
    ].join(",")));
  }

  getTextRevealScopes(root = document) {
    const scopeSelector = ".site-chrome, #MainContent, .ardea-footer";
    if (root !== document) {
      const rootScope = root.matches?.(scopeSelector) ? root : root.closest?.(scopeSelector);
      if (rootScope) return [rootScope];
      return Array.from(document.querySelectorAll(scopeSelector)).filter((scope) => scope.contains(root));
    }
    return Array.from(document.querySelectorAll(scopeSelector));
  }

  assignTextRevealGroupIndexes(targets) {
    const groups = [];
    targets.forEach((target) => {
      const group = this.getTextRevealGroup(target);
      if (!group) return;
      if (!groups.includes(group)) groups.push(group);
      group.dataset.textRevealGroupIndex = String(groups.indexOf(group));
    });
  }

  observeTextRevealTargets(targets) {
    if (!("IntersectionObserver" in window)) {
      targets.forEach((target) => this.revealText(target));
      return;
    }

    if (!this.textRevealObserver) {
      this.textRevealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.textRevealObserver.unobserve(entry.target);
          this.revealText(entry.target);
        });
      }, { rootMargin: "0px", threshold: 0.12 });
    }

    targets.forEach((target) => {
      if (target.dataset.textRevealComplete === "true") return;
      this.textRevealObserver.observe(target);
    });
  }

  isTextRevealCandidate(element) {
    if (!element || element.dataset.textRevealSkip === "true") return false;
    if (element.children.length > 0) return false;
    if (!element.textContent || !element.textContent.trim()) return false;
    if (!/[\p{L}\p{N}]/u.test(element.textContent)) return false;
    if (this.isTextRevealExcluded(element)) return false;
    return true;
  }

  getTextRevealDelay(element) {
    const explicitDelay = element.getAttribute("data-text-reveal-delay");
    if (explicitDelay != null && explicitDelay !== "") return Number(explicitDelay) || 0;

    const group = this.getTextRevealGroup(element);
    const groupIndex = Number(group?.dataset.textRevealGroupIndex || 0);
    const groupBaseDelay = groupIndex * 700;
    const grid = element.closest(".rhome-featured-grid, .catalog-grid, .rhome-products-grid");

    if (grid) {
      const gridTargets = this.getGroupRevealTargets(grid);
      const targetIndex = Math.max(0, gridTargets.indexOf(element));
      const columns = this.getTextRevealGridColumns(grid);
      const row = Math.floor(targetIndex / columns);
      const column = targetIndex % columns;
      return groupBaseDelay + (row + column) * 55;
    }

    const groupTargets = this.getGroupRevealTargets(group || document.querySelector("#MainContent"));
    const targetIndex = Math.max(0, groupTargets.indexOf(element));
    return groupBaseDelay + targetIndex * 35;
  }

  getTextRevealBlock(element) {
    const blockSelector = [
      ".ardea-announcement",
      "h1",
      "h2",
      "h3",
      "h4",
      "p",
      "li",
      "summary",
      "a"
    ].join(",");

    if (element.dataset.textRevealGenerated === "true" || element.tagName === "STRONG") {
      return element.parentElement?.closest(blockSelector) || element;
    }

    return element.matches(blockSelector) ? element : element.closest(blockSelector) || element;
  }

  getTextRevealCharacterDelays(element, value) {
    const chars = Array.from(String(value || ""));
    const textNode = Array.from(element.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    if (!textNode || typeof document.createRange !== "function") {
      return chars.map((char, index) => (/[\p{L}\p{N}]/u.test(char) ? index * 14 : 0));
    }

    const block = this.getTextRevealBlock(element);
    const blockRect = block.getBoundingClientRect();
    const blockWidth = Math.max(1, blockRect.width);
    const blockHeight = Math.max(1, blockRect.height);
    const radiusStep = Number(element.dataset.textRevealRadiusStep || 1300);
    const range = document.createRange();
    let offset = 0;

    const delays = chars.map((char) => {
      const startOffset = offset;
      offset += char.length;
      if (!/[\p{L}\p{N}]/u.test(char)) return 0;

      range.setStart(textNode, startOffset);
      range.setEnd(textNode, offset);
      const rect = range.getBoundingClientRect();
      const x = Math.max(0, rect.left - blockRect.left);
      const y = Math.max(0, rect.top - blockRect.top);
      const xUnits = x / blockWidth;
      const yUnits = y / blockHeight;
      return Math.round(Math.sqrt((xUnits * xUnits) + (yUnits * yUnits)) * radiusStep);
    });

    range.detach();
    return delays;
  }

  getTextRevealGroup(element) {
    return element.closest([
      ".ardea-announcement",
      ".header",
      "#MainContent > section",
      "#MainContent > div",
      "#MainContent > article",
      "#MainContent > header",
      ".ardea-footer"
    ].join(",")) || document.querySelector("#MainContent");
  }

  getGroupRevealTargets(group) {
    if (!group) return [];
    return Array.from(group.querySelectorAll("[data-text-reveal-order]"))
      .filter((element) => this.isTextRevealCandidate(element));
  }

  getTextRevealGridColumns(grid) {
    return Math.max(1, Math.round(this.getCssNumber(grid, "--text-reveal-columns", 4)));
  }

  getTextRevealSpeed() {
    return 2;
  }

  revealText(target, options = {}) {
    const element = typeof target === "string" ? document.querySelector(target) : target;
    if (!element) return Promise.resolve();
    if (element.dataset.textRevealRunning === "true" && !options.restart) return Promise.resolve();

    const finalText = String(options.text ?? element.dataset.textRevealValue ?? element.textContent ?? "");
    const chars = Array.from(finalText);
    const charset = String(options.charset ?? element.dataset.textRevealCharset ?? this.textRevealCharset);
    const speed = this.getTextRevealSpeed();
    const duration = Number(options.duration ?? element.dataset.textRevealDuration ?? 900) / speed;
    const delay = Number(options.delay ?? element.dataset.textRevealDelay ?? 0) / speed;
    const fps = Number(options.fps ?? element.dataset.textRevealFps ?? 28) * speed;
    const stagger = Number(options.stagger ?? element.dataset.textRevealStagger ?? 28) / speed;
    const characterDelays = (options.characterDelays || element._textRevealCharacterDelays || [])
      .map((characterDelay) => Number(characterDelay) / speed);
    const frameInterval = Math.max(16, 1000 / Math.max(1, fps));
    const revealableIndexes = chars
      .map((char, index) => (/[\p{L}\p{N}]/u.test(char) ? index : null))
      .filter((index) => index !== null);
    const revealableSet = new Set(revealableIndexes);
    const revealSequence = new Map(revealableIndexes.map((index, sequenceIndex) => [index, sequenceIndex]));
    const placeholderChars = chars.map((char) => (
      /[\p{L}\p{N}]/u.test(char)
        ? charset[Math.floor(Math.random() * charset.length)] || char
        : char
    ));
    let lastFrame = 0;

    element.dataset.textRevealValue = finalText;
    element.dataset.textRevealRunning = "true";
    element.dataset.textRevealComplete = "false";
    if (element.dataset.textRevealGenerated !== "true" && !element.getAttribute("aria-label")) {
      element.setAttribute("aria-label", finalText);
    }

    return new Promise((resolve) => {
      const startAt = performance.now() + Math.max(0, delay);

      const render = (now) => {
        if (now < startAt) {
          requestAnimationFrame(render);
          return;
        }

        if (now - lastFrame < frameInterval) {
          requestAnimationFrame(render);
          return;
        }

        lastFrame = now;
        const elapsed = now - startAt;

        element.textContent = chars.map((char, index) => {
          if (!revealableSet.has(index)) return char;
          const sequenceIndex = revealSequence.get(index) || 0;
          const characterStart = Number(characterDelays[index] ?? (sequenceIndex * Math.max(1, stagger)));
          const characterEnd = characterStart + Math.max(1, duration);
          if (elapsed < characterStart) return placeholderChars[index];
          if (elapsed >= characterEnd) return char;
          return charset[Math.floor(Math.random() * charset.length)] || char;
        }).join("");

        const finalCharacterDelay = revealableIndexes.reduce((maxDelay, index) => (
          Math.max(maxDelay, Number(characterDelays[index] ?? 0))
        ), 0);
        const fallbackDuration = Math.max(0, revealableIndexes.length - 1) * Math.max(1, stagger);
        const totalDuration = Math.max(1, duration) + (characterDelays.length ? finalCharacterDelay : fallbackDuration);
        if (elapsed < totalDuration) {
          requestAnimationFrame(render);
          return;
        }

        element.textContent = finalText;
        element.dataset.textRevealRunning = "false";
        element.dataset.textRevealComplete = "true";
        resolve();
      };

      requestAnimationFrame(render);
    });
  }

  randomizeTextRevealValue(value, charset = this.textRevealCharset) {
    return Array.from(String(value || "")).map((char) => {
      if (!/[\p{L}\p{N}]/u.test(char)) return char;
      return charset[Math.floor(Math.random() * charset.length)] || char;
    }).join("");
  }

  isInstagramViewerEnabled(trigger) {
    const feed = trigger?.closest("#insta-feed") || document.querySelector("#insta-feed");
    return this.getCssFlag(feed, "--instagram-viewer-enabled");
  }

  bindInstagramVideoLoading() {
    const feed = document.querySelector("#insta-feed");
    if (!feed || this.instagramFeedElement === feed) return;

    this.instagramFeedMutationObserver?.disconnect();
    this.instagramFeedElement = feed;

    const prepareVideos = () => {
      feed.querySelectorAll("[data-instafeed-open-id]").forEach((trigger) => {
        this.prepareInstagramVideo(trigger);
      });
    };

    this.instagramFeedMutationObserver = new MutationObserver(prepareVideos);
    this.instagramFeedMutationObserver.observe(feed, { childList: true, subtree: true });
    prepareVideos();
  }

  getInstagramVideoElements(trigger) {
    const item = trigger?.closest(".instafeed-new-layout-item") || trigger;
    const lightboxId = trigger?.getAttribute("data-instafeed-open-id") || "";
    const lightbox = document.getElementById(lightboxId);
    const tileImage = item?.querySelector("img:not([src*='instagram-icon'])");
    const video = lightbox?.querySelector(".instafeed-post-image video");
    return { tileImage, video };
  }

  getInstagramPosterSource(tileImage) {
    if (!tileImage) return "";
    const sources = [
      tileImage.currentSrc,
      tileImage.getAttribute("src"),
      tileImage.dataset.instafeedSrc
    ];

    return sources.find((source) => (
      source
      && !source.includes("/placeholder.png")
      && !source.includes("instagram-icon")
    )) || tileImage.dataset.instafeedSrc || "";
  }

  isInstagramPosterLoaded(tileImage) {
    const source = tileImage?.currentSrc || tileImage?.getAttribute("src") || "";
    return Boolean(
      tileImage?.complete
      && tileImage.naturalWidth > 0
      && !source.includes("/placeholder.png")
      && !source.includes("instagram-icon")
    );
  }

  prepareInstagramVideo(trigger) {
    const { tileImage, video } = this.getInstagramVideoElements(trigger);
    if (!tileImage || !video || video.dataset.ardeaInstagramPrepared === "true") return;

    video.dataset.ardeaInstagramPrepared = "true";
    video.preload = "none";

    const posterReady = () => {
      const poster = this.getInstagramPosterSource(tileImage);
      if (poster) video.poster = poster;
      this.queueInstagramVideoPreload(video);
    };

    if (this.isInstagramPosterLoaded(tileImage)) {
      posterReady();
    } else {
      const handleTileLoad = () => {
        if (!this.isInstagramPosterLoaded(tileImage)) return;
        tileImage.removeEventListener("load", handleTileLoad);
        posterReady();
      };
      tileImage.addEventListener("load", handleTileLoad);
    }
  }

  isInstagramVideoPreloadConstrained() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return false;
    return Boolean(
      connection.saveData
      || ["slow-2g", "2g", "3g"].includes(connection.effectiveType)
      || (Number.isFinite(connection.downlink) && connection.downlink < 1.5)
    );
  }

  queueInstagramVideoPreload(video) {
    if (
      !video
      || video.dataset.ardeaInstagramQueued === "true"
      || this.isInstagramVideoPreloadConstrained()
    ) return;

    video.dataset.ardeaInstagramQueued = "true";
    this.instagramVideoPreloadQueue.push(video);
    this.drainInstagramVideoPreloadQueue();
  }

  waitForInstagramVideoIdle() {
    return new Promise((resolve) => {
      window.setTimeout(() => {
        if ("requestIdleCallback" in window) {
          window.requestIdleCallback(resolve, { timeout: 1500 });
        } else {
          resolve();
        }
      }, 750);
    });
  }

  waitForInstagramVideoMetadata(video) {
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) return Promise.resolve();

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        video.removeEventListener("loadedmetadata", finish);
        video.removeEventListener("error", finish);
        resolve();
      };
      const timeout = window.setTimeout(finish, 3000);
      video.addEventListener("loadedmetadata", finish, { once: true });
      video.addEventListener("error", finish, { once: true });
    });
  }

  async drainInstagramVideoPreloadQueue() {
    if (this.instagramVideoPreloadRunning) return;
    this.instagramVideoPreloadRunning = true;

    while (this.instagramVideoPreloadQueue.length) {
      const video = this.instagramVideoPreloadQueue.shift();
      await this.waitForInstagramVideoIdle();

      if (
        !video?.isConnected
        || video.dataset.ardeaInstagramPriority === "true"
        || this.isInstagramVideoPreloadConstrained()
      ) continue;

      video.preload = "metadata";
      video.load();
      await this.waitForInstagramVideoMetadata(video);
    }

    this.instagramVideoPreloadRunning = false;
  }

  prioritizeInstagramVideo(trigger) {
    const { tileImage, video } = this.getInstagramVideoElements(trigger);
    if (!video) return;

    const poster = this.getInstagramPosterSource(tileImage);
    if (poster) video.poster = poster;
    video.dataset.ardeaInstagramPriority = "true";
    video.preload = "auto";
    video.load();
  }

  bindInstagramViewer() {
    document.addEventListener("click", (event) => {
      const trigger = event.target.closest("#insta-feed [data-instafeed-open-id]");
      if (!trigger) return;

      if (!this.isInstagramViewerEnabled(trigger)) {
        this.prioritizeInstagramVideo(trigger);
        return;
      }

      const post = this.getInstagramViewerPost(trigger);
      if (!post?.mediaSrc) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.openInstagramViewer(post);
    }, true);

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.instagramViewerHistoryActive) {
        event.preventDefault();
        this.closeInstagramViewer();
        return;
      }

      if (event.key !== "Enter" && event.key !== " ") return;
      const trigger = event.target.closest?.("#insta-feed [data-instafeed-open-id]");
      if (!trigger) return;

      if (!this.isInstagramViewerEnabled(trigger)) {
        this.prioritizeInstagramVideo(trigger);
        return;
      }

      const post = this.getInstagramViewerPost(trigger);
      if (!post?.mediaSrc) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.openInstagramViewer(post);
    }, true);

    window.addEventListener("popstate", () => {
      if (!this.instagramViewerHistoryActive) return;
      this.closeInstagramViewer({ fromHistory: true });
    });
  }

  getInstagramViewerPost(trigger) {
    const item = trigger.closest(".instafeed-new-layout-item") || trigger;
    const lightbox = document.getElementById(trigger.getAttribute("data-instafeed-open-id") || "");
    const tileImage = item.querySelector("img:not([src*='instagram-icon'])");
    const appMedia = lightbox?.querySelector(".instafeed-post-image video, .instafeed-post-image img:not(.profile-picture)");
    const mediaElement = appMedia || tileImage;
    const mediaType = mediaElement?.tagName === "VIDEO" ? "video" : "image";
    const mediaSrc = mediaType === "video"
      ? mediaElement?.currentSrc || mediaElement?.getAttribute("src") || mediaElement?.querySelector("source")?.src || mediaElement?.poster
      : mediaElement?.currentSrc || mediaElement?.getAttribute("src") || mediaElement?.dataset.instafeedSrc;
    const profileLink = lightbox?.querySelector(".instafeed-header a.follow[href], a.follow[href], a[href*='instagram.com']");

    return {
      mediaType,
      mediaSrc,
      poster: mediaElement?.poster || tileImage?.currentSrc || tileImage?.src || "",
      alt: mediaElement?.getAttribute("alt") || tileImage?.getAttribute("alt") || "Instagram post",
      caption: lightbox?.querySelector(".instafeed-caption")?.textContent.trim() || this.getCaptionFromInstagramAlt(tileImage?.getAttribute("alt")),
      date: lightbox?.querySelector(".post-date")?.textContent.trim() || "",
      profileUrl: profileLink?.href || "",
      profileLabel: lightbox?.querySelector(".instafeed-fullname")?.textContent.trim() || "ardea.la"
    };
  }

  getCaptionFromInstagramAlt(alt = "") {
    const match = String(alt).match(/caption:\s*([\s\S]*?)(?:\.\.\.)?$/i);
    return (match?.[1] || "").replace(/&amp;/g, "&").trim();
  }

  getInstagramViewer() {
    if (this.instagramViewer) return this.instagramViewer;

    const viewer = document.createElement("div");
    viewer.className = "ardea-instagram-viewer";
    viewer.setAttribute("role", "dialog");
    viewer.setAttribute("aria-modal", "true");
    viewer.setAttribute("aria-label", "Instagram post");
    viewer.hidden = true;
    viewer.innerHTML = `
      <button class="ardea-instagram-viewer__close" type="button" aria-label="Close Instagram post" data-instagram-viewer-close></button>
      <div
        class="ardea-instagram-viewer__media"
        role="button"
        tabindex="0"
        aria-label="Close Instagram post"
        data-instagram-viewer-media
      ></div>
      <div class="ardea-instagram-viewer__meta">
        <div class="ardea-instagram-viewer__line">
          <a class="ardea-instagram-viewer__profile" data-instagram-viewer-profile target="_blank" rel="noopener nofollow"></a>
          <span class="ardea-instagram-viewer__date" data-instagram-viewer-date></span>
        </div>
        <p class="ardea-instagram-viewer__caption" data-instagram-viewer-caption></p>
      </div>
    `;

    viewer.querySelector("[data-instagram-viewer-close]").addEventListener("click", () => this.closeInstagramViewer());
    const viewerMedia = viewer.querySelector("[data-instagram-viewer-media]");
    viewerMedia.addEventListener("click", () => this.closeInstagramViewer());
    viewerMedia.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      this.closeInstagramViewer();
    });
    viewer.addEventListener("click", (event) => {
      if (event.target === viewer) this.closeInstagramViewer();
    });
    document.body.appendChild(viewer);
    this.instagramViewer = viewer;
    return viewer;
  }

  openInstagramViewer(post) {
    const viewer = this.getInstagramViewer();
    const media = viewer.querySelector("[data-instagram-viewer-media]");
    const profile = viewer.querySelector("[data-instagram-viewer-profile]");
    const date = viewer.querySelector("[data-instagram-viewer-date]");
    const caption = viewer.querySelector("[data-instagram-viewer-caption]");

    media.replaceChildren();
    const mediaElement = post.mediaType === "video" ? document.createElement("video") : document.createElement("img");
    mediaElement.className = "ardea-instagram-viewer__asset";
    if (post.mediaType === "video") {
      mediaElement.autoplay = true;
      mediaElement.controls = false;
      mediaElement.loop = true;
      mediaElement.muted = true;
      mediaElement.playsInline = true;
      mediaElement.preload = "auto";
      mediaElement.poster = post.poster;
    } else {
      mediaElement.alt = post.alt;
    }
    mediaElement.src = post.mediaSrc;
    media.appendChild(mediaElement);
    if (post.mediaType === "video") {
      mediaElement.play().catch(() => {});
    }

    profile.textContent = post.profileLabel;
    profile.href = post.profileUrl || "https://www.instagram.com/ardea.la";
    date.textContent = post.date;
    date.hidden = !post.date;
    caption.textContent = post.caption;
    caption.hidden = !post.caption;

    viewer.hidden = false;
    viewer.classList.add("ardea-instagram-viewer--open");
    document.documentElement.classList.add("ardea-instagram-viewer-lock");

    if (!window.history.state?.ardeaInstagramViewer) {
      const currentState = window.history.state && typeof window.history.state === "object"
        ? { ...window.history.state }
        : {};
      delete currentState.ardeaInstafeedLightbox;
      window.history.pushState({ ...currentState, ardeaInstagramViewer: true }, "", window.location.href);
    }
    this.instagramViewerHistoryActive = true;
    viewer.querySelector("[data-instagram-viewer-close]")?.focus({ preventScroll: true });
  }

  closeInstagramViewer(options = {}) {
    if (!this.instagramViewerHistoryActive && !this.instagramViewer) return;

    const viewer = this.instagramViewer;
    viewer?.classList.remove("ardea-instagram-viewer--open");
    if (viewer) {
      viewer.hidden = true;
      viewer.querySelector("[data-instagram-viewer-media]")?.replaceChildren();
    }
    document.documentElement.classList.remove("ardea-instagram-viewer-lock");
    this.instagramViewerHistoryActive = false;

    if (!options.fromHistory && window.history.state?.ardeaInstagramViewer) {
      window.history.back();
    }
  }

  getOpenInstafeedLightbox() {
    return Array.from(document.querySelectorAll(".instafeed-lightbox")).find((lightbox) => {
      const style = window.getComputedStyle(lightbox);
      const rect = lightbox.getBoundingClientRect();
      return style.display !== "none" && style.visibility !== "hidden" && rect.width > 0 && rect.height > 0;
    });
  }

  bindInstafeedLightboxHistory() {
    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-instafeed-open-id]")) {
        window.setTimeout(() => {
          if (!this.getOpenInstafeedLightbox()) return;
          if (window.history.state?.ardeaInstafeedLightbox) {
            this.instafeedHistoryActive = true;
            return;
          }

          const currentState = window.history.state && typeof window.history.state === "object"
            ? window.history.state
            : {};
          window.history.pushState({ ...currentState, ardeaInstafeedLightbox: true }, "", window.location.href);
          this.instafeedHistoryActive = true;
        }, 80);
      }

      if (event.target.closest(".instafeed-lightbox .instafeed-close-button")) {
        window.setTimeout(() => {
          if (this.instafeedHistoryActive && window.history.state?.ardeaInstafeedLightbox) {
            window.history.back();
          }
        }, 0);
      }
    });

    window.addEventListener("popstate", () => {
      if (!this.instafeedHistoryActive) return;

      this.instafeedHistoryActive = false;
      const lightbox = this.getOpenInstafeedLightbox();
      lightbox?.querySelector(".instafeed-close-button")?.click();
    });
  }

  bindHeader() {
    const searchRoot = document.querySelector("[data-search-focus]");
    const searchTrigger = document.querySelector("[data-search-focus-open]");
    let searchCloseTimer = null;
    let searchRequestController = null;
    let searchDebounceTimer = null;
    let searchRequestId = 0;
    let closeShopMenu = () => {};

    const clearSearchCloseTimer = () => {
      if (searchCloseTimer) {
        window.clearTimeout(searchCloseTimer);
        searchCloseTimer = null;
      }
    };

    const openSearch = (options = {}) => {
      if (!searchRoot) return;
      clearSearchCloseTimer();
      closeShopMenu();
      searchRoot.hidden = false;
      searchRoot.classList.add("search-focus--open");
      document.documentElement.classList.add("search-lock");
      document.body.classList.add("search-lock");
      const input = searchRoot.querySelector("input[type='search']");
      if (options.focusInput !== false && input) {
        input.focus({ preventScroll: true });
      }
    };

    const closeSearch = () => {
      if (!searchRoot) return;
      clearSearchCloseTimer();
      searchRoot.classList.remove("search-focus--open");
      document.documentElement.classList.remove("search-lock");
      document.body.classList.remove("search-lock");
      searchCloseTimer = window.setTimeout(() => {
        if (!searchRoot.classList.contains("search-focus--open")) {
          searchRoot.hidden = true;
        }
      }, 260);
    };

    closeShopMenu = this.bindShopMenu(closeSearch);

    if (searchTrigger && searchRoot) {
      searchTrigger.addEventListener("click", (event) => {
        event.preventDefault();
        openSearch();
      });
      searchRoot.addEventListener("pointerenter", () => clearSearchCloseTimer());
      searchRoot.addEventListener("pointerleave", (event) => {
        if (event.pointerType === "touch") return;
        closeSearch();
      });
    }

    if (searchRoot) {
      const searchInput = searchRoot.querySelector("[data-search-focus-input]");
      const searchGrid = searchRoot.querySelector("[data-search-results]");
      const searchEmpty = searchRoot.querySelector("[data-search-empty]");
      const searchStatus = searchRoot.querySelector("[data-search-status]");
      const searchIndexNode = searchRoot.querySelector("[data-search-products]");
      let localSearchProducts = [];

      try {
        localSearchProducts = JSON.parse(searchIndexNode?.textContent || "[]");
      } catch (_error) {
        localSearchProducts = [];
      }

      const normalizeSearchValue = (value) => String(value || "").trim().toLowerCase();
      const getLocalSearchResults = (query) => {
        const tokens = normalizeSearchValue(query).split(/\s+/).filter(Boolean);
        if (!tokens.length || !localSearchProducts.length) return [];

        return localSearchProducts
          .filter((product) => {
            const haystack = normalizeSearchValue([
              product.title,
              product.vendor,
              product.type,
              product.tags
            ].join(" "));
            return tokens.every((token) => haystack.includes(token));
          })
          .slice(0, 4);
      };

      const renderSearchProducts = (products) => {
        if (!searchGrid || !searchEmpty || !searchStatus) return;

        searchRoot.classList.toggle("search-focus--has-results", products.length > 0);

        if (!products.length) {
          searchGrid.hidden = true;
          searchGrid.innerHTML = "";
          searchEmpty.hidden = false;
          searchStatus.textContent = searchInput?.value.trim() ? "No results" : "Search ARDEA";
          searchEmpty.textContent = searchInput?.value.trim() ? "No products matched your search." : "Type to preview matching products.";
          return;
        }

        searchStatus.textContent = `${products.length} ${products.length === 1 ? "result" : "results"}`;
        searchEmpty.hidden = true;
        searchGrid.hidden = false;
        searchGrid.innerHTML = products.map((product, index) => this.renderSearchProduct(product, index)).join("");
      };

      const renderSearchProductsWhenReady = async (products, requestId, query) => {
        if (!products.length) {
          renderSearchProducts([]);
          return;
        }

        if (searchStatus) searchStatus.textContent = "Loading";
        if (searchEmpty) searchEmpty.hidden = true;

        await this.preloadSearchProductImages(products);

        if (requestId !== searchRequestId || query !== (searchInput?.value.trim() || "")) return;
        renderSearchProducts(products);
      };

      const runPredictiveSearch = async () => {
        const query = searchInput?.value.trim() || "";
        const requestId = ++searchRequestId;

        if (!query) {
          if (searchRequestController) searchRequestController.abort();
          renderSearchProducts([]);
          return;
        }

        if (localSearchProducts.length) {
          await renderSearchProductsWhenReady(getLocalSearchResults(query), requestId, query);
          return;
        }

        if (searchRequestController) searchRequestController.abort();
        searchRequestController = new AbortController();
        if (searchStatus) searchStatus.textContent = "Searching";

        try {
          const params = new URLSearchParams({
            q: query,
            "resources[type]": "product",
            "resources[limit]": "4",
            "resources[options][unavailable_products]": "last"
          });
          const response = await fetch(`/search/suggest.json?${params.toString()}`, {
            headers: { "Accept": "application/json" },
            signal: searchRequestController.signal
          });
          if (!response.ok) throw new Error("Predictive search failed");
          const payload = await response.json();
          if (requestId !== searchRequestId || query !== (searchInput?.value.trim() || "")) return;
          await renderSearchProductsWhenReady(payload?.resources?.results?.products || [], requestId, query);
        } catch (error) {
          if (error.name === "AbortError") return;
          if (requestId !== searchRequestId) return;
          renderSearchProducts([]);
        }
      };

      searchInput?.addEventListener("input", () => {
        window.clearTimeout(searchDebounceTimer);
        searchDebounceTimer = window.setTimeout(runPredictiveSearch, 180);
      });
    }

    document.querySelectorAll("[data-search-toggle]").forEach((button) => {
      button.addEventListener("click", () => {
        if (!searchRoot) return;
        if (searchRoot.hidden || !searchRoot.classList.contains("search-focus--open")) {
          openSearch();
        } else {
          closeSearch();
        }
      });
    });

    document.querySelectorAll("[data-cart-open]").forEach((button) => {
      button.addEventListener("click", () => this.openCart());
    });

    document.querySelectorAll("[data-cart-close], [data-cart-underlay]").forEach((button) => {
      button.addEventListener("click", () => this.closeCart());
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        const panel = document.querySelector("[data-search-popover]");
        if (panel && !panel.hasAttribute("hidden")) {
          closeSearch();
        }
        closeShopMenu();
        this.closeCart();
      }
    });
  }

  bindShopMenu(closeSearch = () => {}) {
    const shopShell = document.querySelector("[data-shop-menu-shell]");
    const shopTrigger = shopShell?.querySelector("[data-shop-menu-trigger]");
    const shopMenu = shopShell?.querySelector("[data-shop-menu]");
    let shopCloseTimer = null;

    if (!shopShell || !shopTrigger || !shopMenu) return () => {};

    this.bindShopMenuPreviews(shopMenu);

    const clearShopCloseTimer = () => {
      if (shopCloseTimer) {
        window.clearTimeout(shopCloseTimer);
        shopCloseTimer = null;
      }
    };

    const openShopMenu = () => {
      clearShopCloseTimer();
      closeSearch();
      document.documentElement.classList.add("shop-lock");
      shopShell.classList.add("header--shop-open");
      shopTrigger.setAttribute("aria-expanded", "true");
    };

    const closeShopMenu = () => {
      clearShopCloseTimer();
      shopShell.classList.remove("header--shop-open");
      shopTrigger.setAttribute("aria-expanded", "false");
      document.documentElement.classList.remove("shop-lock");
    };

    const scheduleShopClose = () => {
      clearShopCloseTimer();
      shopCloseTimer = window.setTimeout(() => {
        if (!shopShell.contains(document.activeElement)) {
          closeShopMenu();
        }
      }, 140);
    };

    shopTrigger.addEventListener("pointerenter", (event) => {
      if (event.pointerType === "touch") return;
      openShopMenu();
    });
    shopMenu.addEventListener("pointerenter", openShopMenu);
    shopShell.addEventListener("pointerleave", scheduleShopClose);
    shopShell.addEventListener("focusin", (event) => {
      if (event.target.closest("[data-shop-menu-trigger], [data-shop-menu]")) {
        openShopMenu();
      }
    });
    shopShell.addEventListener("focusout", () => {
      window.setTimeout(() => {
        if (!shopShell.contains(document.activeElement)) {
          closeShopMenu();
        }
      }, 0);
    });
    shopShell.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && shopShell.classList.contains("header--shop-open")) {
        event.preventDefault();
        closeShopMenu();
        shopTrigger.focus();
      }
    });

    return closeShopMenu;
  }

  bindShopMenuPreviews(shopMenu) {
    const previewsRoot = shopMenu.querySelector("[data-shop-menu-previews]");
    const dataNode = shopMenu.querySelector("[data-shop-menu-preview-json]");
    if (!previewsRoot || !dataNode) return;

    let previewSets = {};
    try {
      previewSets = JSON.parse(dataNode.textContent || "{}");
    } catch (_error) {
      previewSets = {};
    }

    let previewRequestId = 0;

    const getPreviewImageSources = () => (
      [...new Set(Object.values(previewSets).flatMap((items) => (
        Array.isArray(items) ? items.map((item) => item.image).filter(Boolean) : []
      )))]
    );

    const markPreviewsReady = () => {
      previewsRoot.querySelectorAll(".shop-mega__preview").forEach((preview) => {
        preview.classList.add("is-ready");
      });
      previewsRoot.removeAttribute("aria-busy");
    };

    const markInitialPreviewsReady = async () => {
      const requestId = ++previewRequestId;
      previewsRoot.setAttribute("aria-busy", "true");
      await this.waitForImagesIn(previewsRoot);
      if (requestId !== previewRequestId) return;
      markPreviewsReady();
    };

    this.preloadImages(getPreviewImageSources());
    markInitialPreviewsReady();

    const renderPreviewSet = async (key = "__default__") => {
      const requestId = ++previewRequestId;
      const items = previewSets[key] || previewSets.__default__ || [];
      const showPreviewTitles = !key.startsWith("product:");

      previewsRoot.setAttribute("aria-busy", "true");

      const previewNodes = items.map((item, index) => {
        const link = document.createElement("a");
        link.className = "shop-mega__preview is-ready";
        link.href = item.href || "#";
        if (item.label) link.setAttribute("aria-label", item.label);

        const media = document.createElement("span");
        media.className = "shop-mega__preview-media";

        if (item.image) {
          const image = document.createElement("img");
          image.className = "shop-mega__preview-image";
          image.src = item.image;
          image.alt = item.imageAlt || item.label || "";
          image.loading = "eager";
          image.decoding = "async";
          media.appendChild(image);
        }

        if (showPreviewTitles) {
          const title = document.createElement("span");
          title.className = "shop-mega__preview-title";
          title.textContent = item.label || "";
          link.append(media, title);
        } else {
          link.append(media);
        }
        return link;
      });

      await this.waitForImages(Array.from(previewNodes).flatMap((node) => (
        Array.from(node.querySelectorAll("img"))
      )));

      if (requestId !== previewRequestId) return;
      previewsRoot.replaceChildren(...previewNodes);
      previewsRoot.removeAttribute("aria-busy");
    };

    let activePreviewKey = "";

    const activatePreviewLink = (link) => {
      const key = link.getAttribute("data-shop-menu-preview-key");
      if (!key || key === activePreviewKey) return;
      activePreviewKey = key;
      renderPreviewSet(key);
    };

    shopMenu.addEventListener("pointerover", (event) => {
      const link = event.target.closest("[data-shop-menu-preview-key]");
      if (!link || !shopMenu.contains(link)) return;
      activatePreviewLink(link);
    });

    shopMenu.querySelectorAll("[data-shop-menu-preview-key]").forEach((link) => {
      link.addEventListener("focus", () => activatePreviewLink(link));
    });

    shopMenu.addEventListener("mouseleave", () => {
      activePreviewKey = "__default__";
      renderPreviewSet("__default__");
    });
    shopMenu.addEventListener("focusout", () => {
      window.setTimeout(() => {
        if (!shopMenu.contains(document.activeElement)) {
          activePreviewKey = "__default__";
          renderPreviewSet("__default__");
        }
      }, 0);
    });
  }

  bindProductForms(root = document) {
    const forms = [
      ...(root.matches?.("[data-product-form]") ? [root] : []),
      ...root.querySelectorAll("[data-product-form]")
    ];

    forms.forEach((form) => {
      if (form.dataset.productFormBound === "true") return;
      form.dataset.productFormBound = "true";

      form.addEventListener("change", () => this.syncVariant(form));
      form.querySelector("[data-back-in-stock-trigger]")?.addEventListener("click", () => {
        this.openBackInStockForm(form);
      });
      form.addEventListener("submit", async (event) => {
        if (event.submitter?.closest(".shopify-payment-button")) return;

        event.preventDefault();
        const submit = form.querySelector("[type='submit']");
        if (submit) submit.disabled = true;

        try {
          const item = await this.addCartItem(new FormData(form), "product-page");
          await this.refreshCart(item);
          this.openCart();
        } catch (error) {
          this.showCartError(error);
          form.querySelector("[data-form-error]")?.replaceChildren(document.createTextNode(error.message || "Unable to add this item. Please try again."));
        } finally {
          if (submit) submit.disabled = false;
        }
      });
      const variant = this.syncVariant(form);
      const notifyRequested = new URL(window.location.href).searchParams.get("notify") === "1";
      if (notifyRequested && variant && !variant.available && this.notificationsEnabled) {
        this.openBackInStockForm(form);
      } else if (notifyRequested) {
        this.syncProductVariantUrl(variant?.id, true);
      }
    });

    const quickAddButtons = [
      ...(root.matches?.("[data-quick-add]") ? [root] : []),
      ...root.querySelectorAll("[data-quick-add]")
    ];

    quickAddButtons.forEach((button) => {
      if (button.dataset.quickAddBound === "true") return;
      button.dataset.quickAddBound = "true";

      button.addEventListener("click", async () => {
        if (button.disabled) return;
        const variantId = button.getAttribute("data-quick-add");
        button.disabled = true;
        try {
          const body = new FormData();
        body.append("id", variantId);
        body.append("quantity", "1");
          const item = await this.addCartItem(body, "quick-add");
          await this.refreshCart(item);
          this.openCart();
        } catch (error) {
          this.showCartError(error);
        } finally {
          button.disabled = false;
        }
      });
    });

    const backInStockLinks = [
      ...(root.matches?.("[data-back-in-stock-link]") ? [root] : []),
      ...root.querySelectorAll("[data-back-in-stock-link]")
    ];

    backInStockLinks.forEach((button) => {
      if (button.dataset.backInStockLinkBound === "true") return;
      button.dataset.backInStockLinkBound = "true";
      if (this.notificationsEnabled) {
        button.disabled = false;
        button.textContent = "Notify me";
      }
      button.addEventListener("click", () => {
        if (!this.notificationsEnabled) return;
        const target = button.getAttribute("data-back-in-stock-link");
        if (!target) return;
        const url = new URL(target, window.location.origin);
        window.location.assign(url);
      });
    });

    this.bindQuickAddSelectors(root);
  }

  bindQuickAddSelectors(root = document) {
    const selectors = [
      ...(root.matches?.("[data-quick-add-selector]") ? [root] : []),
      ...root.querySelectorAll("[data-quick-add-selector]")
    ];

    selectors.forEach((selector) => {
      if (selector.dataset.quickAddSelectorBound === "true") return;

      const trigger = selector.querySelector("[data-quick-add-trigger]");
      const picker = selector.querySelector("[data-quick-add-picker]");
      const sizeButtons = Array.from(selector.querySelectorAll("[data-quick-add-size]"));
      const variantsJson = selector.querySelector("[data-quick-add-variants]");
      const sizeOptionIndex = Number(selector.getAttribute("data-size-option-index"));
      let variants = [];

      try {
        variants = JSON.parse(variantsJson?.textContent || "[]");
      } catch (error) {
        console.error("Unable to read quick-add variants.", error);
      }

      if (!trigger || !picker || !sizeButtons.length || !variants.length || !Number.isInteger(sizeOptionIndex)) return;

      const baseVariantId = selector.getAttribute("data-base-variant-id");
      const baseVariant = variants.find((variant) => String(variant.id) === String(baseVariantId))
        || variants.find((variant) => variant.available)
        || variants[0];
      const hasAvailableVariant = variants.some((variant) => variant.available);
      if (!hasAvailableVariant) {
        trigger.disabled = !this.notificationsEnabled;
        trigger.textContent = this.notificationsEnabled ? "Notify me" : "Sold out";
        trigger.setAttribute(
          "aria-label",
          this.notificationsEnabled
            ? "Choose a size to get notified when this product is back"
            : "This product is sold out"
        );
      }

      sizeButtons.forEach((button) => {
        const size = button.getAttribute("data-quick-add-size");
        const variant = variants.find((candidate) => {
          if (candidate.options?.[sizeOptionIndex] !== size) return false;
          return candidate.options?.every((value, index) => index === sizeOptionIndex || value === baseVariant?.options?.[index]);
        });

        button.dataset.quickAddVariantId = variant?.id || "";
        button.dataset.quickAddAvailable = variant ? String(variant.available) : "";
        const disabled = !variant || (!variant.available && !this.notificationsEnabled);
        button.disabled = disabled;
        button.setAttribute("aria-disabled", String(disabled));
        button.setAttribute("aria-label", variant && !variant.available ? `${size} — sold out; notify me` : size);
      });

      trigger.addEventListener("click", () => {
        const isOpen = trigger.getAttribute("aria-expanded") === "true";
        this.closeQuickAddSelectors(isOpen ? null : selector);

        if (!isOpen) {
          selector.classList.add("is-open");
          trigger.setAttribute("aria-expanded", "true");
          picker.hidden = false;
          (
            sizeButtons.find((button) => !button.disabled && button.dataset.quickAddAvailable !== "false")
            || sizeButtons.find((button) => !button.disabled)
          )?.focus();
        }
      });

      selector.addEventListener("keydown", (event) => {
        if (event.key !== "Escape") return;
        this.closeQuickAddSelectors();
        trigger.focus();
      });

      sizeButtons.forEach((button) => {
        button.addEventListener("click", async () => {
          const variantId = button.dataset.quickAddVariantId;
          if (!variantId || button.disabled) return;

          if (button.dataset.quickAddAvailable === "false") {
            const productUrl = selector.getAttribute("data-product-url");
            if (!productUrl) return;
            const url = new URL(productUrl, window.location.origin);
            url.searchParams.set("variant", variantId);
            url.searchParams.set("notify", "1");
            window.location.assign(url);
            return;
          }

          trigger.disabled = true;
          sizeButtons.forEach((sizeButton) => {
            sizeButton.disabled = true;
          });

          try {
            const body = new FormData();
            body.append("id", variantId);
            body.append("quantity", "1");
            const item = await this.addCartItem(body, "quick-add-size");
            await this.refreshCart(item);
            this.closeQuickAddSelectors();
            this.openCart();
          } catch (error) {
            this.closeQuickAddSelectors();
            this.showCartError(error);
          } finally {
            trigger.disabled = false;
            sizeButtons.forEach((sizeButton) => {
              sizeButton.disabled = !sizeButton.dataset.quickAddVariantId
                || (sizeButton.dataset.quickAddAvailable === "false" && !this.notificationsEnabled);
            });
          }
        });
      });

      selector.dataset.quickAddSelectorBound = "true";
    });

    if (this.quickAddDismissBound) return;
    this.quickAddDismissBound = true;
    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-quick-add-selector]")) return;
      this.closeQuickAddSelectors();
    });
  }

  closeQuickAddSelectors(except = null) {
    document.querySelectorAll("[data-quick-add-selector].is-open").forEach((selector) => {
      if (selector === except) return;
      selector.classList.remove("is-open");
      const trigger = selector.querySelector("[data-quick-add-trigger]");
      const picker = selector.querySelector("[data-quick-add-picker]");
      if (trigger) trigger.setAttribute("aria-expanded", "false");
      if (picker) picker.hidden = true;
    });
  }

  bindPolicyPages() {
    const policy = document.querySelector(".shopify-policy__container");
    const policyBody = policy?.querySelector(".shopify-policy__body .rte");
    if (!policy || !policyBody || policy.dataset.ardeaEnhanced === "true") return;

    policy.dataset.ardeaEnhanced = "true";
    policy.classList.add("content", "content--faq", "content--policy");

    const title = policy.querySelector(".shopify-policy__title");
    if (title) title.classList.add("page-title");

    const body = policy.querySelector(".shopify-policy__body");
    if (body) body.classList.add("content-card");

    const accordion = document.createElement("div");
    accordion.className = "faq-accordion";
    const sections = [];
    let current = { title: "Returns & Exchanges", nodes: [] };

    Array.from(policyBody.childNodes).forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        if (node.textContent.trim()) current.nodes.push(node.cloneNode(true));
        return;
      }

      const element = node;
      const strong = element.querySelector(":scope > strong:first-child");
      const heading = strong?.textContent?.trim();

      if (heading) {
        if (current.nodes.length) sections.push(current);
        current = { title: heading, nodes: [] };
        const clone = element.cloneNode(true);
        this.resetTextRevealState(clone);
        const cloneStrong = clone.querySelector(":scope > strong:first-child");
        cloneStrong?.remove();
        clone.innerHTML = clone.innerHTML.replace(/^\s*<br\s*\/?>\s*/i, "").trim();
        if (clone.textContent.trim()) current.nodes.push(clone);
        return;
      }

      if (element.textContent.trim()) {
        const clone = element.cloneNode(true);
        this.resetTextRevealState(clone);
        current.nodes.push(clone);
      }
    });

    if (current.nodes.length) sections.push(current);
    if (!sections.length) return;

    sections.forEach((section) => {
      const details = document.createElement("details");
      details.className = "faq-item";
      details.id = section.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

      const summary = document.createElement("summary");
      summary.textContent = section.title;
      details.append(summary);

      const answer = document.createElement("div");
      answer.className = "faq-item__answer rte";
      section.nodes.forEach((node) => answer.append(node));
      details.append(answer);
      accordion.append(details);
    });

    policyBody.replaceChildren(accordion);
    policyBody.classList.remove("rte");
  }

  resetTextRevealState(root) {
    const nodes = [
      ...(root.dataset?.textRevealBound === "true" || root.dataset?.textRevealValue ? [root] : []),
      ...(root.querySelectorAll?.("[data-text-reveal-bound], [data-text-reveal-value]") || [])
    ];

    nodes.forEach((node) => {
      if (node.dataset.textRevealValue) {
        node.textContent = node.dataset.textRevealValue;
      }
      delete node.dataset.textRevealBound;
      delete node.dataset.textRevealRunning;
      delete node.dataset.textRevealComplete;
      delete node.dataset.textRevealOrder;
      delete node.dataset.textRevealDelay;
      delete node.dataset.textRevealDuration;
      delete node.dataset.textRevealFps;
      delete node.dataset.textRevealStagger;
      delete node.dataset.textRevealGroupIndex;
      delete node.dataset.textRevealValue;
      node.removeAttribute("aria-label");
    });
  }

  bindAboutPage() {
    const root = document.querySelector("[data-about-page]");
    const pin = document.querySelector("[data-about-pin]");
    const stage = document.querySelector("[data-about-stage]");
    const title = document.querySelector("[data-about-title]");
    const floats = Array.from(document.querySelectorAll("[data-about-float]"));
    const panels = Array.from(document.querySelectorAll("[data-about-panel]"));
    if (!root || !pin || !stage || !title) return;

    let ticking = false;
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    const mix = (from, to, amount) => from + ((to - from) * amount);
    const smooth = (value) => value * value * (3 - (2 * value));
    const revealPanelText = (panel) => {
      if (panel.dataset.aboutTextRevealPlayed === "true") return;
      panel.dataset.aboutTextRevealPlayed = "true";

      panel.querySelectorAll("[data-text-reveal-order]").forEach((target) => {
        if (!this.isTextRevealCandidate(target)) return;
        this.revealText(target, { restart: true });
      });
    };

    const updateAboutParallax = () => {
      const rootStyles = window.getComputedStyle(root);
      const isPinned = rootStyles.getPropertyValue("--about-pinned").trim() === "1";
      const headerOffset = parseFloat(rootStyles.getPropertyValue("--about-header-offset")) || 0;

      if (!isPinned) {
        stage.classList.remove("is-fixed", "is-bottom");
        root.style.removeProperty("--about-progress");
        title.style.removeProperty("--about-title-top");
        title.style.removeProperty("--about-title-scale");
        floats.forEach((node) => {
          node.style.removeProperty("--about-float-x");
          node.style.removeProperty("--about-float-y");
        });
        panels.forEach((node) => {
          node.style.removeProperty("--about-panel-opacity");
          node.style.removeProperty("--about-panel-y");
        });
        ticking = false;
        return;
      }

      const pinTop = pin.getBoundingClientRect().top + window.scrollY;
      const stickyHeight = Math.max(1, stage.clientHeight);
      const start = pinTop - headerOffset;
      const end = pinTop + pin.offsetHeight - stickyHeight - headerOffset;
      const progress = clamp((window.scrollY - start) / Math.max(1, end - start), 0, 1);
      const settle = smooth(clamp(progress / 0.18, 0, 1));
      root.style.setProperty("--about-progress", progress.toFixed(4));

      stage.classList.remove("is-fixed", "is-bottom");

      if (window.scrollY >= end) {
        stage.classList.add("is-bottom");
      } else if (window.scrollY >= start) {
        stage.classList.add("is-fixed");
      }

      title.style.setProperty("--about-title-top", `${mix(50, 22, settle).toFixed(2)}%`);
      title.style.setProperty("--about-title-scale", mix(1.34, 1, settle).toFixed(4));

      floats.forEach((node) => {
        const localStart = parseFloat(node.getAttribute("data-start") || "0");
        const localEnd = parseFloat(node.getAttribute("data-end") || "1");
        const local = clamp((progress - localStart) / Math.max(0.001, localEnd - localStart), 0, 1);
        const eased = smooth(local);
        const xSpeed = parseFloat(node.getAttribute("data-speed-x") || "0");
        const travelScale = parseFloat(node.getAttribute("data-travel") || "1");
        const offsetY = parseFloat(node.getAttribute("data-offset-y") || "0");
        const x = (eased - 0.5) * xSpeed;
        const travel = (stickyHeight + node.offsetHeight) * travelScale;
        const y = mix(travel, -travel, eased) + offsetY;
        node.style.setProperty("--about-float-x", `${x.toFixed(2)}px`);
        node.style.setProperty("--about-float-y", `${y.toFixed(2)}px`);
      });

      panels.forEach((panel, index) => {
        const panelStart = 0.22 + (index * 0.17);
        const isLastPanel = index === panels.length - 1;
        const local = (progress - panelStart) / (isLastPanel ? 0.2 : 0.17);
        const active = clamp(local, 0, 1);
        const y = isLastPanel ? mix(78, 0, active) : mix(78, -78, active);
        const opacity = isLastPanel
          ? clamp(local * 3, 0, 1)
          : Math.min(clamp(local * 3, 0, 1), clamp((1 - local) * 3, 0, 1));
        panel.style.setProperty("--about-panel-opacity", opacity.toFixed(3));
        panel.style.setProperty("--about-panel-y", `${y.toFixed(2)}px`);
        if (opacity > 0.04) revealPanelText(panel);
      });

      ticking = false;
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateAboutParallax);
    };

    window.addEventListener("scroll", requestUpdate, { passive: true });
    if ("ResizeObserver" in window) {
      this.aboutResizeObserver = new ResizeObserver(requestUpdate);
      [root, pin, stage, ...floats].forEach((node) => this.aboutResizeObserver.observe(node));
    }
    updateAboutParallax();
  }

  getSearchImageUrl(image, width) {
    if (!image) return "";

    try {
      const url = new URL(image, window.location.href);
      const isShopifyImage = /(^|\.)cdn\.shopify\.com$/.test(url.hostname) || url.pathname.includes("/cdn/shop/");

      if (!isShopifyImage) return image;

      url.searchParams.set("width", String(width));

      if (image.startsWith("//")) {
        return `//${url.host}${url.pathname}${url.search}`;
      }

      return url.href;
    } catch (_error) {
      return image;
    }
  }

  getSearchImageSrcset(image) {
    if (!image) return "";
    return [180, 360, 540]
      .map((width) => `${this.getSearchImageUrl(image, width)} ${width}w`)
      .join(", ");
  }

  getSearchProductImage(product) {
    const image = typeof product.featured_image === "string"
      ? product.featured_image
      : product.featured_image?.url || product.image || "";

    return this.getSearchImageUrl(image, 360);
  }

  preloadImages(sources) {
    return Promise.all(sources.filter(Boolean).map((source) => {
      if (this.imageReadyCache.has(source)) {
        return this.imageReadyCache.get(source);
      }

      const image = new Image();
      const ready = this.waitForImage(image);
      this.imageReadyCache.set(source, ready);
      image.src = source;
      return ready;
    }));
  }

  waitForImage(image) {
    const source = image.currentSrc || image.src;
    if (source && this.imageReadyCache.has(source)) {
      return this.imageReadyCache.get(source);
    }

    return new Promise((resolve) => {
      let settled = false;

      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      const decodeAndResolve = () => {
        if (!image.decode) {
          done();
          return;
        }

        image.decode().catch(() => {}).finally(done);
      };

      if (image.complete && (image.currentSrc || image.src)) {
        decodeAndResolve();
        return;
      }

      image.addEventListener("load", decodeAndResolve, { once: true });
      image.addEventListener("error", done, { once: true });
    });
  }

  waitForImages(images) {
    return Promise.all(Array.from(images || []).map((image) => this.waitForImage(image)));
  }

  waitForImagesIn(root) {
    if (!root) return Promise.resolve();

    return this.waitForImages(root.querySelectorAll("img"));
  }

  preloadSearchProductImages(products) {
    const sources = products
      .map((product) => this.getSearchProductImage(product))
      .filter(Boolean);

    return this.preloadImages(sources);
  }

  renderSearchProduct(product, index = 0) {
    const imageSrc = this.getSearchProductImage(product);
    const imageSrcset = this.getSearchImageSrcset(imageSrc);
    const price = product.price ? this.formatMoney(product.price) : "";
    const compareAtPrice = product.compare_at_price && product.compare_at_price > product.price
      ? this.formatMoney(product.compare_at_price)
      : "";

    return `
      <a class="search-focus__card" href="${this.escape(product.url || "#")}">
        <span class="search-focus__card-media">
          ${imageSrc ? `<img class="search-focus__card-image" src="${this.escape(imageSrc)}" srcset="${this.escape(imageSrcset)}" sizes="(max-width: 720px) 50vw, 25vw" width="360" height="480" loading="${index < 4 ? "eager" : "lazy"}" decoding="async" alt="${this.escape(product.title)}">` : ""}
        </span>
        <span class="search-focus__card-copy">
          <strong class="search-focus__card-title">${this.escape(product.title)}</strong>
          <span class="search-focus__card-price">
            ${price ? `<span class="search-focus__card-price-current">${this.escape(price)}</span>` : ""}
            ${compareAtPrice ? `<span class="search-focus__card-price-compare">${this.escape(compareAtPrice)}</span>` : ""}
          </span>
        </span>
      </a>
    `;
  }

  bindProductGalleries(root = document) {
    const galleries = [
      ...(root.matches?.("[data-product-gallery]") ? [root] : []),
      ...root.querySelectorAll("[data-product-gallery]")
    ];

    galleries.forEach((gallery) => {
      if (gallery.dataset.productGalleryBound === "true") return;
      gallery.dataset.productGalleryBound = "true";
      const track = gallery.querySelector("[data-product-gallery-track]");
      if (!track) return;

      let activeIndex = 0;
      const items = Array.from(track.children);
      const status = gallery.querySelector("[data-gallery-status]");
      const dots = Array.from(gallery.querySelectorAll("[data-gallery-dot]"));
      const thumbs = Array.from(gallery.querySelectorAll("[data-gallery-thumb]"));
      let scrollFrame = null;

      const updateProgress = () => {
        if (status) status.textContent = `${activeIndex + 1} / ${items.length}`;
        items.forEach((item, index) => {
          item.querySelectorAll("[data-product-zoom-open]").forEach((button) => {
            if (index === activeIndex) {
              button.removeAttribute("tabindex");
            } else {
              button.setAttribute("tabindex", "-1");
            }
          });
        });
        dots.forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.classList.toggle("product-gallery-progress__dot--active", isActive);
          if (isActive) {
            dot.setAttribute("aria-current", "true");
          } else {
            dot.removeAttribute("aria-current");
          }
        });
        thumbs.forEach((thumb, index) => {
          const isActive = index === activeIndex;
          thumb.classList.toggle("product-gallery-thumb--active", isActive);
          if (isActive) {
            thumb.setAttribute("aria-current", "true");
            if (thumb.offsetParent !== null) {
              thumb.scrollIntoView({ block: "nearest", inline: "center" });
            }
          } else {
            thumb.removeAttribute("aria-current");
          }
        });
      };

      const setIndex = (nextIndex, options = {}) => {
        if (!items.length) return;
        activeIndex = (nextIndex + items.length) % items.length;
        track.style.setProperty("--product-gallery-offset", `${activeIndex * -100}%`);
        const usesNativeScroll = getComputedStyle(track).overflowX !== "hidden";
        if (!usesNativeScroll && track.scrollLeft) {
          track.scrollLeft = 0;
        }
        if (options.scroll !== false && usesNativeScroll && track.scrollWidth > track.clientWidth) {
          track.scrollTo({
            left: activeIndex * track.clientWidth,
            behavior: options.instant ? "auto" : "smooth"
          });
        }
        updateProgress();
      };

      gallery.querySelectorAll("[data-gallery-next]").forEach((button) => {
        button.addEventListener("click", () => {
          setIndex(activeIndex + 1);
        });
      });

      gallery.querySelectorAll("[data-gallery-prev]").forEach((button) => {
        button.addEventListener("click", () => {
          setIndex(activeIndex - 1);
        });
      });

      dots.forEach((dot) => {
        dot.addEventListener("click", () => {
          setIndex(Number(dot.getAttribute("data-gallery-index") || 0));
        });
      });

      thumbs.forEach((thumb) => {
        thumb.addEventListener("click", () => {
          setIndex(Number(thumb.getAttribute("data-gallery-index") || 0));
        });
      });

      track.addEventListener("scroll", () => {
        if (getComputedStyle(track).overflowX === "hidden") return;
        if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(() => {
          const itemWidth = Math.max(1, track.clientWidth);
          const nextIndex = Math.min(items.length - 1, Math.max(0, Math.round(track.scrollLeft / itemWidth)));
          if (nextIndex !== activeIndex) {
            activeIndex = nextIndex;
            track.style.setProperty("--product-gallery-offset", `${activeIndex * -100}%`);
            updateProgress();
          }
        });
      }, { passive: true });

      updateProgress();
    });
  }

  bindProductImageZoom() {
    if (this.isProductImageZoomEnabled(document.querySelector(".product-page"))) {
      this.preloadProductZoomImages();
    }

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-product-zoom-open]");
      if (!trigger) return;
      if (!this.isProductImageZoomEnabled(trigger)) return;

      const src = trigger.getAttribute("data-product-zoom-src");
      if (!src) return;

      event.preventDefault();
      const modal = this.getProductZoomModal();
      const image = modal.querySelector("[data-product-zoom-image]");
      if (!image) return;

      const previewImage = trigger.querySelector("img");
      const previewSrc = previewImage?.currentSrc || previewImage?.src || "";
      const zoomSrcReady = this.productZoomReadySources.has(src);
      image.alt = trigger.getAttribute("data-product-zoom-alt") || "";
      image.src = zoomSrcReady ? src : previewSrc || src;
      if (typeof modal.showModal === "function") {
        modal.showModal();
      } else {
        modal.setAttribute("open", "");
      }
      document.documentElement.classList.add("product-zoom-lock");
    });

    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-product-zoom-close]")) {
        this.closeProductZoom(event.target.closest("[data-product-zoom-modal]"));
      }
    });

    document.addEventListener("click", (event) => {
      let modal = event.target.matches?.("[data-product-zoom-modal]") ? event.target : null;
      const mobileBackdropTarget = event.target.matches?.("[data-product-zoom-scroller]")
        && this.isProductImageZoomBackdropCloseEnabled();
      if (!modal && mobileBackdropTarget) {
        modal = event.target.closest("[data-product-zoom-modal]");
      }
      if (modal) this.closeProductZoom(modal);
    });

    document.addEventListener("cancel", (event) => {
      if (!event.target.matches?.("[data-product-zoom-modal]")) return;
      document.documentElement.classList.remove("product-zoom-lock");
    }, true);

    document.addEventListener("close", (event) => {
      if (!event.target.matches?.("[data-product-zoom-modal]")) return;
      this.resetProductZoomModal(event.target);
    }, true);
  }

  isProductImageZoomEnabled(context) {
    const productPage = context?.matches?.(".product-page")
      ? context
      : context?.closest?.(".product-page");
    return this.getCssFlag(productPage, "--product-image-zoom-enabled");
  }

  isProductImageZoomBackdropCloseEnabled() {
    return this.getCssFlag(
      document.querySelector(".product-page"),
      "--product-image-zoom-backdrop-close-enabled"
    );
  }

  isProductImageZoomImageCloseEnabled() {
    return this.getCssFlag(
      document.querySelector(".product-page"),
      "--product-image-zoom-image-close-enabled"
    );
  }

  preloadProductZoomImages() {
    const triggers = Array.from(document.querySelectorAll("[data-product-zoom-open]"));
    const sources = Array.from(new Set(triggers.map((trigger) => trigger.getAttribute("data-product-zoom-src")).filter(Boolean)));
    if (!sources.length) return;

    const preload = () => {
      this.preloadProductZoomSources(sources.slice(0, 1));
      sources.slice(1).forEach((source, index) => {
        window.setTimeout(() => this.preloadProductZoomSources([source]), 300 * (index + 1));
      });
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(preload, { timeout: 1600 });
      return;
    }

    window.setTimeout(preload, 900);
  }

  preloadProductZoomSources(sources) {
    this.preloadImages(sources).then(() => {
      sources.forEach((source) => this.productZoomReadySources.add(source));
    });
  }

  getProductZoomModal() {
    let modal = document.querySelector("[data-product-zoom-modal]");
    if (modal) {
      this.bindProductZoomClickZoom(modal);
      return modal;
    }

    modal = document.createElement("dialog");
    modal.className = "product-zoom-modal";
    modal.setAttribute("data-product-zoom-modal", "");
    modal.setAttribute("data-text-reveal-skip", "");
    modal.setAttribute("aria-label", "Product image");
    modal.innerHTML = `
      <button class="product-zoom-modal__close" type="button" data-product-zoom-close aria-label="Close product image"></button>
      <div class="product-zoom-modal__scroller" data-product-zoom-scroller>
        <img class="product-zoom-modal__image" data-product-zoom-image alt="">
      </div>
    `;
    document.body.appendChild(modal);
    this.bindProductZoomClickZoom(modal);
    return modal;
  }

  bindProductZoomClickZoom(modal) {
    if (modal.dataset.productZoomClickZoomBound === "true") return;
    const image = modal.querySelector("[data-product-zoom-image]");
    if (!image) return;
    const scroller = modal.querySelector("[data-product-zoom-scroller]");
    modal.dataset.productZoomClickZoomBound = "true";

    image.addEventListener("click", (event) => {
      event.stopPropagation();
      if (this.isProductImageZoomImageCloseEnabled()) {
        this.closeProductZoom(modal);
        return;
      }

      const isMagnifying = modal.classList.contains("product-zoom-modal--magnifying");
      if (isMagnifying) {
        modal.classList.remove("product-zoom-modal--magnifying");
        image.style.removeProperty("--product-zoom-x");
        image.style.removeProperty("--product-zoom-y");
        image.style.removeProperty("--product-zoom-pan-x");
        scroller?.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        return;
      }

      const rect = image.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / Math.max(1, rect.width)) * 100));
      const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / Math.max(1, rect.height)) * 100));
      image.style.setProperty("--product-zoom-x", `${x.toFixed(2)}%`);
      image.style.setProperty("--product-zoom-y", `${y.toFixed(2)}%`);
      image.style.setProperty("--product-zoom-pan-x", `${(-35 * (x / 100)).toFixed(2)}%`);
      modal.classList.add("product-zoom-modal--magnifying");
      window.requestAnimationFrame(() => {
        const zoomedHeight = image.getBoundingClientRect().height;
        const scrollElement = scroller || modal;
        const targetTop = (y / 100) * zoomedHeight - (scrollElement.clientHeight / 2);
        const maxTop = Math.max(0, scrollElement.scrollHeight - scrollElement.clientHeight);
        scrollElement.scrollTo({
          top: Math.min(maxTop, Math.max(0, targetTop)),
          left: 0,
          behavior: "auto"
        });
      });
    });
  }

  closeProductZoom(modal) {
    if (!modal) return;
    if (typeof modal.close === "function") {
      modal.close();
    } else {
      modal.removeAttribute("open");
    }
    this.resetProductZoomModal(modal);
  }

  resetProductZoomModal(modal) {
    if (!modal) return;
    const image = modal.querySelector("[data-product-zoom-image]");
    const scroller = modal.querySelector("[data-product-zoom-scroller]");
    if (image) {
      image.removeAttribute("src");
      image.style.removeProperty("--product-zoom-x");
      image.style.removeProperty("--product-zoom-y");
      image.style.removeProperty("--product-zoom-pan-x");
    }
    modal.classList.remove("product-zoom-modal--magnifying");
    scroller?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.classList.remove("product-zoom-lock");
  }

  bindProductSizeCharts() {
    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-size-chart-open]")) {
        const modal = document.querySelector("[data-size-chart-modal]");
        if (!modal) return;
        event.preventDefault();
        if (typeof modal.showModal === "function") {
          modal.showModal();
        } else {
          modal.setAttribute("open", "");
        }
        document.documentElement.classList.add("product-size-chart-lock");
      }

      if (event.target.closest("[data-size-chart-close]")) {
        this.closeProductSizeChart(event.target.closest("[data-size-chart-modal]"));
      }
    });

    document.addEventListener("cancel", (event) => {
      if (!event.target.matches?.("[data-size-chart-modal]")) return;
      document.documentElement.classList.remove("product-size-chart-lock");
    }, true);

    document.addEventListener("click", (event) => {
      const modal = event.target.matches?.("[data-size-chart-modal]") ? event.target : null;
      if (modal) this.closeProductSizeChart(modal);
    });
  }

  closeProductSizeChart(modal) {
    if (!modal) return;
    if (typeof modal.close === "function") {
      modal.close();
    } else {
      modal.removeAttribute("open");
    }
    document.documentElement.classList.remove("product-size-chart-lock");
  }

  getProductVariants(form) {
    try {
      return JSON.parse(form.querySelector("[data-variants-json]")?.textContent || "[]");
    } catch (error) {
      console.error("Unable to read product variants.", error);
      return [];
    }
  }

  syncProductVariantUrl(variantId, clearNotify = false) {
    if (!variantId) return;
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    if (clearNotify) url.searchParams.delete("notify");
    window.history.replaceState(window.history.state, "", url);
  }

  getNotificationsEnabled() {
    return Boolean(document.body.dataset.notificationBackendUrl)
      && document.body.dataset.notificationsEnabled === "true";
  }

  async submitNotification(path, payload) {
    if (!this.notificationsEnabled || !this.notificationBackendUrl) {
      throw new Error("notifications_unavailable");
    }

    // Keep the same submission identity after a timeout or failed response.
    // A retry must not look like new consent after a later unsubscribe.
    this.notificationSubmissionIds ||= new Map();
    const submissionKey = JSON.stringify([path, payload]);
    const submissionId = this.notificationSubmissionIds.get(submissionKey) || crypto.randomUUID();
    this.notificationSubmissionIds.set(submissionKey, submissionId);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(`${this.notificationBackendUrl}${path}`, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, submissionId }),
        signal: controller.signal
      });
      let body = {};
      try {
        body = await response.json();
      } catch (_error) {
        // The status code remains enough to present a useful retry message.
      }
      if (!response.ok) {
        const error = new Error(body.error || `request_failed_${response.status}`);
        error.status = response.status;
        throw error;
      }
      this.notificationSubmissionIds.delete(submissionKey);
      return body;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  bindWelcomePopup() {
    const modal = document.querySelector("[data-welcome-modal]");
    const form = modal?.querySelector("[data-marketing-signup-form]");
    if (
      !modal
      || !form
      || !this.notificationsEnabled
      || document.body.dataset.welcomePopupEnabled !== "true"
      || this.hasCompletedWelcomeSignup()
    ) return;

    const offerTab = document.querySelector("[data-welcome-offer-tab]");
    if (offerTab) {
      offerTab.hidden = false;
      offerTab.addEventListener("click", () => this.openWelcomePopup("tab"));
    }

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;

      const submit = form.querySelector("[type='submit']");
      const status = form.querySelector("[data-form-status]");
      const data = new FormData(form);
      if (submit) submit.disabled = true;
      if (status) {
        status.classList.remove("is-error");
        status.textContent = "Joining…";
      }

      try {
        const signup = await this.submitNotification("/v1/marketing-signups", {
          email: data.get("email"),
          company: data.get("company"),
          consent: true,
          source: "welcome-popup"
        });
        this.rememberWelcomeSignup();
        const discountCode = document.body.dataset.welcomeDiscountCode?.trim()
          || (typeof signup.discountCode === "string" && signup.discountCode.trim()
            ? signup.discountCode.trim()
            : "WELCOME10");
        const discountApplied = await this.applyWelcomeDiscount(discountCode);
        if (status) {
          status.textContent = discountApplied
            ? `${discountCode} has been applied to your cart.`
            : `Your signup request is saved. Use ${discountCode} at checkout.`;
        }
        form.classList.add("is-complete");
        this.captureAnalytics("user signed up", {
          signup_type: "newsletter",
          provider: "resend",
          form_id: "native-welcome-popup"
        });
      } catch (error) {
        if (status) {
          status.classList.add("is-error");
          status.textContent = this.notificationErrorMessage(error);
        }
      } finally {
        if (submit) submit.disabled = form.classList.contains("is-complete");
      }
    });

    // Dismissal pauses automatic prompts, not the shopper's ability to reopen.
    if (this.hasRecentWelcomeDismissal()) return;
    const delaySeconds = Number(document.body.dataset.welcomePopupDelay) || 10;
    const scrollPercent = Number(document.body.dataset.welcomePopupScroll) || 40;
    window.setTimeout(() => this.openWelcomePopup("time"), delaySeconds * 1000);

    const openAfterScroll = () => {
      const scrollable = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      if ((window.scrollY / scrollable) * 100 < scrollPercent) return;
      window.removeEventListener("scroll", openAfterScroll);
      this.openWelcomePopup("scroll");
    };
    window.addEventListener("scroll", openAfterScroll, { passive: true });
  }

  openWelcomePopup(trigger) {
    if (
      this.hasCompletedWelcomeSignup()
      || (trigger !== "tab" && (this.welcomePopupOpened || this.hasRecentWelcomeDismissal()))
      || document.querySelector("dialog[open]")
      || (window.matchMedia("(max-width: 749px)").matches
        && document.documentElement.classList.contains("cart-drawer-open"))
    ) return;
    const modal = document.querySelector("[data-welcome-modal]");
    if (!modal || modal.querySelector("form.is-complete")) return;

    this.welcomePopupOpened = true;
    this.openEmailModal(modal);
    this.captureAnalytics("welcome form opened", { trigger });
  }

  getWelcomeStorageTimestamp(key) {
    try {
      return Number(window.localStorage.getItem(key)) || 0;
    } catch (_error) {
      return 0;
    }
  }

  hasRecentWelcomeDismissal() {
    const dismissedAt = this.getWelcomeStorageTimestamp("ardea:welcome-dismissed");
    const cooldownDays = Number(document.body.dataset.welcomePopupCooldown) || 30;
    return dismissedAt > 0 && Date.now() - dismissedAt < cooldownDays * 24 * 60 * 60 * 1000;
  }

  hasCompletedWelcomeSignup() {
    return this.getWelcomeStorageTimestamp("ardea:welcome-subscribed") > 0;
  }

  rememberWelcomeDismissal() {
    if (this.hasCompletedWelcomeSignup()) return;
    try {
      window.localStorage.setItem("ardea:welcome-dismissed", String(Date.now()));
    } catch (_error) {
      // Storage restrictions should not prevent dismissal.
    }
  }

  rememberWelcomeSignup() {
    const offerTab = document.querySelector("[data-welcome-offer-tab]");
    if (offerTab) offerTab.hidden = true;
    try {
      window.localStorage.setItem("ardea:welcome-subscribed", String(Date.now()));
      window.localStorage.removeItem("ardea:welcome-dismissed");
    } catch (_error) {
      // The backend remains the source of truth for completed signups.
    }
  }

  async applyWelcomeDiscount(discountCode) {
    try {
      const discountDetails = (application) => application.discount_application || application;
      const configuredCodes = (this.cart?.discount_codes || [])
        .map((discount) => String(discount.code || "").trim())
        .filter(Boolean);
      const appliedCodes = (this.cart?.cart_level_discount_applications || [])
        .map(discountDetails)
        .filter((application) => application.type === "discount_code")
        .map((application) => String(application.title || "").trim())
        .filter(Boolean);
      const activeCodes = [...new Set([...configuredCodes, ...appliedCodes])];
      if (!activeCodes.some((code) => code.toLowerCase() === discountCode.toLowerCase())) {
        activeCodes.push(discountCode);
      }

      const response = await fetch(this.cartRoute("cart/update.js"), {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ discount: activeCodes.join(",") })
      });
      const cart = await this.parseCartJsonResponse(response);
      if (!response.ok || !cart) return false;
      const configuredDiscount = (cart.discount_codes || []).find((discount) => (
        String(discount.code || "").toLowerCase() === discountCode.toLowerCase()
      ));
      if (!configuredDiscount) return false;

      this.cart = cart;
      this.renderCart();
      await this.ensureCartAcceleratedCheckout();
      return true;
    } catch (_error) {
      return false;
    }
  }

  bindBackInStockModal() {
    const modal = document.querySelector("[data-back-in-stock-modal]");
    const form = modal?.querySelector("[data-back-in-stock-form]");
    form?.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;

      const submit = form.querySelector("[type='submit']");
      const status = form.querySelector("[data-form-status]");
      const data = new FormData(form);
      if (submit) submit.disabled = true;
      if (status) {
        status.classList.remove("is-error");
        status.textContent = "Saving your request…";
      }

      try {
        await this.submitNotification("/v1/restock-requests", {
          email: data.get("email"),
          company: data.get("company"),
          productId: form.dataset.productId,
          variantId: form.dataset.variantId,
          marketingConsent: data.get("marketingConsent") === "on"
        });
        this.rememberBackInStockRequest(form.dataset.variantId);
        document.querySelectorAll("[data-product-form]").forEach((productForm) => {
          if (String(productForm.querySelector("[name='id']")?.value) !== String(form.dataset.variantId)) return;
          this.syncBackInStockButton(productForm.querySelector("[data-back-in-stock-trigger]"), {
            id: form.dataset.variantId,
            available: false
          });
        });
        if (status) status.textContent = "You're on the list. We'll email you when it's back.";
        form.classList.add("is-complete");
        this.captureAnalytics("back in stock form submitted", {
          product_id: form.dataset.productId,
          variant_id: form.dataset.variantId,
          marketing_consent: data.get("marketingConsent") === "on"
        });
      } catch (error) {
        if (status) {
          status.classList.add("is-error");
          status.textContent = error.message === "variant_available"
            ? "Good news—this item is available now. Refresh the page to purchase it."
            : this.notificationErrorMessage(error);
        }
      } finally {
        if (submit) submit.disabled = form.classList.contains("is-complete");
      }
    });

    document.addEventListener("click", (event) => {
      const close = event.target.closest("[data-email-modal-close]");
      if (close) {
        this.closeEmailModal(close.closest("[data-email-modal]"));
        return;
      }
      const modalBackdrop = event.target.matches?.("[data-email-modal]") ? event.target : null;
      if (modalBackdrop) this.closeEmailModal(modalBackdrop);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      const modal = document.querySelector("[data-email-modal][open]");
      if (!modal) return;
      event.preventDefault();
      this.closeEmailModal(modal);
    });

    document.addEventListener("cancel", (event) => {
      if (!event.target.matches?.("[data-email-modal]")) return;
      if (event.target.matches("[data-welcome-modal]")) this.rememberWelcomeDismissal();
      document.documentElement.classList.remove("email-modal-lock");
    }, true);

    document.addEventListener("close", (event) => {
      if (!event.target.matches?.("[data-email-modal]")) return;
      document.documentElement.classList.remove("email-modal-lock");
    }, true);
  }

  notificationErrorMessage(error) {
    if (error?.name === "AbortError") return "This is taking too long. Please try again.";
    if (error?.message === "rate_limited") return "Please wait a little before trying again.";
    return "We couldn't save that yet. Please try again.";
  }

  openEmailModal(modal) {
    if (!modal) return;
    if (typeof modal.showModal === "function") {
      modal.showModal();
    } else {
      modal.setAttribute("open", "");
    }
    document.documentElement.classList.add("email-modal-lock");
  }

  closeEmailModal(modal) {
    if (!modal) return;
    if (modal.matches("[data-welcome-modal]")) this.rememberWelcomeDismissal();
    if (typeof modal.close === "function") {
      modal.close();
    } else {
      modal.removeAttribute("open");
    }
    document.documentElement.classList.remove("email-modal-lock");
  }

  getBackInStockStorageKey(variantId) {
    return `ardea:back-in-stock:${variantId}`;
  }

  rememberBackInStockRequest(variantId) {
    try {
      window.localStorage.setItem(this.getBackInStockStorageKey(variantId), String(Date.now()));
    } catch (error) {
      console.warn("Unable to remember back in stock request.", error);
    }
  }

  forgetBackInStockRequest(variantId) {
    try {
      window.localStorage.removeItem(this.getBackInStockStorageKey(variantId));
    } catch (error) {
      console.warn("Unable to clear back in stock request.", error);
    }
  }

  hasBackInStockRequest(variantId) {
    try {
      const requestedAt = Number(window.localStorage.getItem(this.getBackInStockStorageKey(variantId)));
      if (!requestedAt) return false;
      if (Date.now() - requestedAt <= this.backInStockRequestMaxAge) return true;
      this.forgetBackInStockRequest(variantId);
    } catch (error) {
      console.warn("Unable to read back in stock request.", error);
    }
    return false;
  }

  syncBackInStockButton(button, variant) {
    if (!button || !variant) return;
    if (variant.available) this.forgetBackInStockRequest(variant.id);
    const requested = !variant.available && this.hasBackInStockRequest(variant.id);
    button.hidden = variant.available || !this.notificationsEnabled;
    button.disabled = requested;
    button.textContent = requested ? "We'll notify you" : "Notify me";
    button.setAttribute(
      "aria-label",
      requested ? "We'll notify you when this item is back in stock" : "Notify me when this item is back in stock"
    );
  }

  openBackInStockForm(form) {
    const variantId = form.querySelector("[name='id']")?.value;
    const variant = this.getProductVariants(form).find((item) => String(item.id) === String(variantId));
    if (!variant || variant.available || !this.notificationsEnabled) return;

    this.syncProductVariantUrl(variant.id, true);
    const notify = form.querySelector("[data-back-in-stock-trigger]");
    if (this.hasBackInStockRequest(variant.id)) {
      this.syncBackInStockButton(notify, variant);
      return;
    }

    const modal = document.querySelector("[data-back-in-stock-modal]");
    const notificationForm = modal?.querySelector("[data-back-in-stock-form]");
    if (!modal || !notificationForm) return;
    notificationForm.dataset.productId = form.getAttribute("data-product-id") || "";
    notificationForm.dataset.variantId = String(variant.id);
    notificationForm.classList.remove("is-complete");
    const notificationSubmit = notificationForm.querySelector("[type='submit']");
    const notificationStatus = notificationForm.querySelector("[data-form-status]");
    const productLabels = modal.querySelectorAll("[data-back-in-stock-product]");
    if (notificationSubmit) notificationSubmit.disabled = false;
    if (notificationStatus) {
      notificationStatus.classList.remove("is-error");
      notificationStatus.textContent = "";
    }
    const productTitle = form.getAttribute("data-product-title") || "this item";
    const variantTitle = variant.title && variant.title !== "Default Title" ? ` — ${variant.title}` : "";
    productLabels.forEach((productLabel) => {
      productLabel.textContent = `${productTitle}${variantTitle}`;
    });
    this.openEmailModal(modal);
    window.setTimeout(() => notificationForm.elements.email?.focus(), 0);
    this.captureAnalytics("back in stock form opened", {
      product_id: form.getAttribute("data-product-id") || undefined,
      product_name: form.getAttribute("data-product-title") || undefined,
      variant_id: String(variant.id),
      variant_name: variant.title || undefined
    });
  }

  syncVariant(form) {
    const variantData = this.getProductVariants(form);
    if (!variantData.length) return null;

    const selectedOptions = Array.from(form.querySelectorAll("[data-option-group]")).map((group) => {
      const checked = group.querySelector("input:checked");
      return checked ? checked.value : null;
    });

    form.querySelectorAll("[data-option-group]").forEach((group, optionIndex) => {
      group.querySelectorAll("input").forEach((input) => {
        const matchingVariants = variantData.filter((item) => {
          if (item.options[optionIndex] !== input.value) return false;
          return item.options.every((option, index) => {
            return index === optionIndex || selectedOptions[index] === null || option === selectedOptions[index];
          });
        });
        const hasVariant = matchingVariants.length > 0;
        const hasAvailableVariant = matchingVariants.some((item) => item.available);
        const isSoldOut = hasVariant && !hasAvailableVariant;
        const optionLabel = input.getAttribute("data-option-value-label") || input.value;

        input.disabled = !hasVariant;
        input.dataset.soldOut = String(isSoldOut);
        input.setAttribute("aria-label", isSoldOut ? `${optionLabel} — sold out` : optionLabel);
      });
    });

    const variant = variantData.find((item) => {
      return item.options.every((option, index) => option === selectedOptions[index]);
    });

    if (!variant) return null;
    const idInput = form.querySelector("[name='id']");
    const submit = form.querySelector("[type='submit']");
    const notify = form.querySelector("[data-back-in-stock-trigger]");
    const purchaseActions = form.querySelector(".product-purchase-actions");
    const paymentStack = form.querySelector(".product-payment-stack");
    const summary = form.closest(".product-summary");
    const price = summary?.querySelector(".product-price");
    const priceWrapper = summary?.querySelector(".price");
    if (idInput) idInput.value = variant.id;
    if (submit) {
      submit.disabled = !variant.available;
      submit.hidden = !variant.available && this.notificationsEnabled;
      submit.textContent = variant.available
        ? (submit.getAttribute("data-add-label") || "Add to cart")
        : (submit.getAttribute("data-sold-out-label") || "Sold out");
    }
    this.syncBackInStockButton(notify, variant);
    if (paymentStack) paymentStack.hidden = !variant.available;
    if (purchaseActions) {
      purchaseActions.classList.toggle("product-purchase-actions--notify", !variant.available);
    }
    if (price) price.textContent = this.formatMoney(variant.price);
    if (priceWrapper) {
      priceWrapper.classList.toggle("price--sale", Number(variant.compare_at_price || 0) > Number(variant.price || 0));
    }
    this.syncProductVariantUrl(variant.id);
    form.dispatchEvent(new CustomEvent("variant:change", {
      bubbles: true,
      detail: { variant }
    }));
    return variant;
  }

  async refreshCart(optimisticItem = null) {
    try {
      const response = await fetch(this.cartRoute("cart.js"), { headers: { "Accept": "application/json" } });
      if (!response.ok) throw new Error("Unable to load cart.");
      this.cart = await response.json();
      this.renderCart();
      await this.ensureCartAcceleratedCheckout();
      return true;
    } catch (_error) {
      this.cart = this.cart || this.createEmptyCart();
      if (optimisticItem) this.mergeCartItem(optimisticItem);
      this.renderCart();
      return false;
    }
  }

  async ensureCartAcceleratedCheckout() {
    if (document.querySelector("[data-cart-accelerated-checkout]")) return true;

    const footer = document.querySelector("[data-cart-footer]");
    if (!footer || !this.cart?.item_count) return false;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set("sections", "ardea-cart-drawer");

      const response = await fetch(url.toString(), {
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });
      if (!response.ok) throw new Error("Unable to refresh cart checkout options.");

      const sections = await response.json();
      const markup = sections?.["ardea-cart-drawer"];
      if (!markup) return false;

      const template = document.createElement("template");
      template.innerHTML = markup;
      const acceleratedCheckout = template.content.querySelector("[data-cart-accelerated-checkout]");
      const checkout = footer.querySelector(".cart-drawer__checkout");
      if (!acceleratedCheckout || !checkout) return false;

      checkout.insertAdjacentElement("afterend", acceleratedCheckout);
      return true;
    } catch (_error) {
      return false;
    }
  }

  async addCartItem(body, source = "storefront") {
    const requestedQuantity = Number(body.get?.("quantity") || 1);
    const response = await fetch(this.cartRoute("cart/add.js"), {
      method: "POST",
      headers: { "Accept": "application/json" },
      body
    });
    const data = await this.parseCartJsonResponse(response);
    if (!response.ok) {
      throw new Error(data?.description || data?.message || "Unable to add this item. Please try again.");
    }

    const items = Array.isArray(data?.items) ? data.items : [data];
    items.filter(Boolean).forEach((item) => {
      const price = Number(item.final_price ?? item.price ?? 0) / 100;
      this.captureAnalytics("product added to cart", {
        source,
        product_id: item.product_id ? String(item.product_id) : undefined,
        variant_id: item.variant_id ? String(item.variant_id) : undefined,
        sku: item.sku || undefined,
        product_name: item.product_title || item.title || undefined,
        variant_name: item.variant_title || undefined,
        quantity: requestedQuantity,
        cart_line_quantity: Number(item.quantity || requestedQuantity),
        price,
        value: price * requestedQuantity,
        currency: window.Shopify?.currency?.active,
        product_url: item.url || undefined
      });
    });

    return data;
  }

  async changeCartLine(lineKey, quantity) {
    const body = JSON.stringify({ id: lineKey, quantity });
    const response = await fetch(this.cartRoute("cart/change.js"), {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json"
      },
      body
    });
    const data = await this.parseCartJsonResponse(response);
    if (!response.ok) {
      throw new Error(data?.description || data?.message || "Unable to update cart. Please try again.");
    }
    this.cart = data;
    this.renderCart();
    await this.ensureCartAcceleratedCheckout();
  }

  async parseCartJsonResponse(response) {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (_error) {
      if (!response.ok) {
        throw new Error("Unable to reach Shopify cart. Please reload and try again.");
      }
      return null;
    }
  }

  createEmptyCart() {
    return {
      item_count: 0,
      items: [],
      total_price: 0
    };
  }

  cartRoute(path) {
    const root = window.Shopify?.routes?.root || "/";
    return `${root.replace(/\/?$/, "/")}${path}`;
  }

  mergeCartItem(item) {
    if (!item) return;
    const cart = this.cart || this.createEmptyCart();
    const existing = cart.items.find((cartItem) => (
      cartItem.key === item.key || String(cartItem.variant_id) === String(item.variant_id || item.id)
    ));

    if (existing) {
      existing.quantity += item.quantity || 1;
      existing.final_line_price = Number(existing.final_line_price || 0) + Number(item.final_line_price || item.line_price || item.price || 0);
    } else {
      cart.items.push({
        key: item.key || String(item.variant_id || item.id),
        id: item.id,
        variant_id: item.variant_id || item.id,
        product_title: item.product_title || item.title,
        variant_title: item.variant_title || item.variant_options?.join(" / ") || "",
        url: item.url,
        image: item.image,
        quantity: item.quantity || 1,
        final_line_price: item.final_line_price || item.line_price || item.price || 0
      });
    }

    cart.item_count = cart.items.reduce((sum, cartItem) => sum + Number(cartItem.quantity || 0), 0);
    cart.total_price = cart.items.reduce((sum, cartItem) => sum + Number(cartItem.final_line_price || 0), 0);
    this.cart = cart;
  }

  formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    return amount.toLocaleString(undefined, { style: "currency", currency: window.Shopify?.currency?.active || "USD" });
  }

  renderCart() {
    const error = document.querySelector("[data-cart-error]");
    if (error) {
      error.hidden = true;
      error.textContent = "";
    }

    const count = this.cart?.item_count || 0;
    document.querySelectorAll("[data-cart-count]").forEach((node) => {
      node.textContent = String(count);
      node.toggleAttribute("hidden", count === 0);
    });
    document.querySelectorAll("[data-cart-title-count]").forEach((node) => {
      node.textContent = `(${count})`;
    });
    this.renderCartProgress();

    const holder = document.querySelector("[data-cart-dynamic]");
    if (!holder) return;

    if (!this.cart || count === 0) {
      holder.innerHTML = `
        <div class="empty-state cart-drawer__empty cart-empty">
          <strong>Your Cart is Empty</strong>
          <a class="cart-drawer__empty-shop" href="/collections/all">Shop</a>
        </div>
      `;
      document.querySelector("[data-cart-footer]")?.setAttribute("hidden", "");
      return;
    }

    holder.innerHTML = `
      <div class="cart-lines cart-drawer__lines">
        ${this.cart.items.map((item) => `
          <article class="cart-line" data-cart-line-key="${this.escape(item.key || String(item.variant_id || item.id))}">
            <a class="cart-line__media" href="${this.escape(item.url || "#")}">
              ${item.image ? `<img class="cart-line__image" src="${item.image}" alt="${this.escape(item.product_title)}">` : ""}
            </a>
            <div class="cart-line__body">
              <a class="cart-line__title" href="${this.escape(item.url || "#")}">${this.escape(item.product_title)}</a>
              ${item.variant_title ? `<div class="cart-line__variant">${this.escape(item.variant_title)}</div>` : ""}
              <button class="cart-line__remove" type="button" data-cart-remove>Remove</button>
            </div>
            <div class="cart-line__controls" aria-label="Quantity">
              <button type="button" data-cart-quantity="-1" aria-label="Decrease quantity">-</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-quantity="1" aria-label="Increase quantity">+</button>
            </div>
            <div class="cart-line__price">${this.formatMoney(item.final_line_price)}</div>
          </article>
        `).join("")}
      </div>
    `;
    document.querySelector("[data-cart-footer]")?.removeAttribute("hidden");
    document.querySelector("[data-cart-subtotal]")?.replaceChildren(document.createTextNode(this.formatMoney(this.cart.total_price)));
  }

  renderCartProgress() {
    const drawer = document.querySelector("[data-cart-drawer]");
    const progress = document.querySelector("[data-cart-progress]");
    if (!drawer || !progress) return;

    const limit = Number(drawer.getAttribute("data-free-shipping-limit") || 0);
    if (!limit) {
      progress.hidden = true;
      return;
    }

    const total = Number(this.cart?.total_price || 0);
    const remaining = Math.max(limit - total, 0);
    const text = progress.querySelector("[data-cart-progress-text]");
    const bar = progress.querySelector("[data-cart-progress-bar]");

    progress.hidden = false;
    if (text) {
      text.textContent = remaining === 0
        ? "Congratulations! Your order qualifies for free shipping"
        : `Spend ${this.formatMoney(remaining)} more for free shipping`;
    }
    if (bar) {
      bar.style.width = `${Math.min(100, Math.round((total / limit) * 100))}%`;
    }
  }

  bindCartControls() {
    document.addEventListener("click", async (event) => {
      const quantityTrigger = event.target.closest("[data-cart-quantity]");
      const removeTrigger = event.target.closest("[data-cart-remove]");
      const line = event.target.closest("[data-cart-line-key]");

      if (!line || (!quantityTrigger && !removeTrigger)) return;

      const lineKey = line.getAttribute("data-cart-line-key");
      const item = this.cart?.items?.find((cartItem) => cartItem.key === lineKey);
      if (!lineKey || !item) return;

      const nextQuantity = removeTrigger
        ? 0
        : Math.max(0, Number(item.quantity || 0) + Number(quantityTrigger.getAttribute("data-cart-quantity") || 0));

      try {
        await this.changeCartLine(lineKey, nextQuantity);
      } catch (error) {
        this.showCartError(error);
      }
    });
  }

  bindGooglePayRecovery() {
    let storefrontWasAway = false;
    let recoveryTimer = null;

    const markStorefrontAway = () => {
      storefrontWasAway = true;
      window.clearTimeout(recoveryTimer);
      recoveryTimer = null;
    };

    const recoverAbandonedGooglePay = () => {
      if (!storefrontWasAway || document.visibilityState === "hidden") return;
      storefrontWasAway = false;
      window.clearTimeout(recoveryTimer);

      recoveryTimer = window.setTimeout(() => {
        recoveryTimer = null;
        if (storefrontWasAway || document.visibilityState === "hidden") return;

        const grayPanes = document.querySelectorAll("gpay-graypane");
        if (!grayPanes.length) return;

        // Chrome on iOS can retain Google Pay's sign-in window after returning
        // to the storefront, leaving its gray pane and wallet button locked.
        grayPanes.forEach((grayPane) => grayPane.remove());
        document.querySelectorAll("shopify-google-pay-button").forEach((googlePayButton) => {
          googlePayButton.disabled = false;
          if (googlePayButton.button) googlePayButton.button.disabled = false;
        });
      }, 200);
    };

    window.addEventListener("blur", markStorefrontAway);
    window.addEventListener("focus", recoverAbandonedGooglePay);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        markStorefrontAway();
      } else {
        recoverAbandonedGooglePay();
      }
    });
    window.addEventListener("pageshow", (event) => {
      if (!event.persisted) return;
      markStorefrontAway();
      recoverAbandonedGooglePay();
    });
  }

  showCartError(error) {
    const message = error?.message || "Unable to update cart. Please try again.";
    const holder = document.querySelector("[data-cart-error]");
    if (holder) {
      holder.textContent = message;
      holder.hidden = false;
    }
    this.openCart();
  }

  escape(value) {
    const div = document.createElement("div");
    div.textContent = value || "";
    return div.innerHTML;
  }

  openCart() {
    document.querySelector("[data-cart-drawer]")?.classList.add("is-open");
    document.querySelector("[data-cart-underlay]")?.classList.add("is-open");
    document.documentElement.classList.add("cart-drawer-open");
    document.documentElement.style.overflow = "hidden";
  }

  closeCart() {
    document.querySelector("[data-cart-drawer]")?.classList.remove("is-open");
    document.querySelector("[data-cart-underlay]")?.classList.remove("is-open");
    document.documentElement.classList.remove("cart-drawer-open");
    document.documentElement.style.overflow = "";
  }
}

document.addEventListener("DOMContentLoaded", () => {
  window.ardeaTheme = new ArdeaTheme();
});
